import React, { useEffect, useState, useMemo } from "react";
import api, { crmApi, fopSettingsApi, salesClientsApi } from "../lib/api";
import ClientCombobox from "../components/ClientCombobox";
import { useCurrency } from "../context/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import SupportPaymentsView from "../components/SupportPaymentsView";
import {
  DollarSign,
  Building2,
  ShieldCheck,
  Server,
  AlertTriangle,
  Users,
  CheckCircle2,
  Clock,
  Eye,
  FileText,
  Upload,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Calendar,
  Filter,
  X,
  Plus,
  Pencil,
  Trash2,
  GripVertical,
  Euro,
  Zap,
  XCircle,
  Send,
  Landmark,
  Unlink,
  FileCheck,
} from "lucide-react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const CrmIncomePage = ({ category = "crm" }) => {
  const { formatAmount } = useCurrency();
  const isSupport = category === "support";
  const pageTitle = isSupport ? "Support Payments" : "CRM Income";
  const pageSubtitle = isSupport
    ? "Monthly support fees by client"
    : "DevBase subscription payments";
  const [clients, setClients] = useState([]);
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [extraStats, setExtraStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Default to current month
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState("payments");
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [fops, setFops] = useState([]);
  const [importing, setImporting] = useState(false);
  const [sentRequests, setSentRequests] = useState(new Set()); // Track sent payment requests
  const [clientRequests, setClientRequests] = useState([]); // Full payment_request rows for tooltip (sender, date)
  
  // Filters
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterClient, setFilterClient] = useState("all");

  // Invoice (Рахунок) dialog
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoicePayment, setInvoicePayment] = useState(null);
  const [selectedFopForInvoice, setSelectedFopForInvoice] = useState("");
  const [invoiceCurrency, setInvoiceCurrency] = useState("");
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [generatingAct, setGeneratingAct] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [downloadingAct, setDownloadingAct] = useState(null);
  const [deletingInvoice, setDeletingInvoice] = useState(null);

  // Bill-to (client billing profile / legal entity) picker — closes backlog f6d26cd9.
  // Mirrors /support-payments: lets the user choose which of the client's
  // billing_profiles[] is the invoice/act recipient (Замовник) for a CRM payment.
  const [billToOpen, setBillToOpen] = useState(false);
  const [billToPayment, setBillToPayment] = useState(null);
  const [billToClient, setBillToClient] = useState(null); // full sales-client doc with billing_profiles[]
  const [billToLoading, setBillToLoading] = useState(false);
  const [billToValue, setBillToValue] = useState("default");
  const [billToSaving, setBillToSaving] = useState(false);

  // Client dialog
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState({
    name: "",
    edrpou: "",
    has_gpt: false,
    support_type: "none",
    support_price: 0,
    deployment_type: "cloud",
    price_per_user: 50,
    users_count: 0,
    notes: "",
    currency: "USD",
    fop: "",
    selected_existing_id: null, // set when user picked an existing client from typeahead
    support_tariff_id: null,
    // Support-specific fields (only rendered when isSupport)
    support_sites: [], // [{url, label}]
    contract_url: "",
    contract_status: "active", // "active" | "needs_renewal" | "missing"
    support_pm_id: null,
    support_pm_name: "",
    // Support product grouping — standard | warranty | hosting. Drives the
    // Warranty / Hosting tabs. (backlog d9bc5c5b)
    support_product_type: "standard",
    // Service period for warranty/hosting — drives the expiry alert. (ad55e8ea)
    support_start_date: "",
    support_end_date: "",
    // Invoice currency — drives sales_service.currency at Generate Month.
    // Issuing FOP comes from the existing `fop` field above (name lookup).
    invoice_currency: "EUR",
  });
  // Staff list used as source for "Support PM" picker (managers + leads + team)
  const [staffList, setStaffList] = useState([]);
  // Live billing preview shown inside Add/Edit Client dialog when isSupport — recomputed
  // whenever tariff or sites count changes. Backend authoritative — calls /support-services/preview.
  const [billingPreview, setBillingPreview] = useState(null);
  const [billingPreviewLoading, setBillingPreviewLoading] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  const [draggedClient, setDraggedClient] = useState(null);
  const [draggedPayment, setDraggedPayment] = useState(null);
  // Full client list across all types — used by Add Client typeahead so we
  // attach a tag to existing clients instead of creating duplicates.
  const [allClients, setAllClients] = useState([]);
  // All managed (crm+support) clients — one-off charge picker on Support Payments
  // must offer every managed client, not only support-tagged ones. (backlog 0755a82e)
  const [managementClients, setManagementClients] = useState([]);
  // Active support tariffs (only fetched/used when category === "support")
  const [supportTariffs, setSupportTariffs] = useState([]);

  // Payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    client_id: "",
    month: "",
    payment_type: "monthly",
    planned_amount: 0,
    actual_amount: null,
    currency: "USD",
    status: "planned",
    act_signed: false,
    services_description: "",
  });
  const [savingPayment, setSavingPayment] = useState(false);
  const [generatingPayments, setGeneratingPayments] = useState(false);

  // Bank transaction linking
  const [linkTransactionDialogOpen, setLinkTransactionDialogOpen] = useState(false);
  const [linkingPaymentId, setLinkingPaymentId] = useState(null);
  const [unmatchedTransactions, setUnmatchedTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  useEffect(() => {
    fetchData();
    fetchFops();
    if (category === "support") fetchStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear, category]);

  // Live billing preview — recompute whenever the dialog is open and either
  // the tariff or the sites array changes. Debounced lightly via 200ms so
  // typing in a URL doesn't fire one request per keystroke.
  useEffect(() => {
    if (!isSupport || !clientDialogOpen) {
      setBillingPreview(null);
      return;
    }
    const sitesCount = (clientForm.support_sites || []).filter(
      (s) => s && (s.url || "").trim()
    ).length;
    const handle = setTimeout(async () => {
      setBillingPreviewLoading(true);
      try {
        const res = await api.post("/support-services/preview", {
          tariff_id: clientForm.support_tariff_id || null,
          support_price: parseFloat(clientForm.support_price) || 0,
          currency: clientForm.currency || "EUR",
          sites_count: sitesCount,
          invoice_currency: clientForm.invoice_currency || null,
        });
        setBillingPreview(res.data);
      } catch (err) {
        setBillingPreview(null);
      } finally {
        setBillingPreviewLoading(false);
      }
    }, 200);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSupport,
    clientDialogOpen,
    clientForm.support_tariff_id,
    clientForm.support_price,
    clientForm.currency,
    clientForm.invoice_currency,
    // serialize sites for dependency tracking
    (clientForm.support_sites || []).map((s) => s.url || "").join("|"),
  ]);

  const fetchFops = async () => {
    try {
      const res = await fopSettingsApi.getAll();
      setFops(res.data || []);
    } catch (err) {
      console.error("Error fetching FOPs:", err);
    }
  };

  // Fetch staff for Support PM picker. Tighten to management-relevant roles
  // — admins, PMs, sales, CRM, art directors. We additionally admit anyone
  // whose free-text `position` field contains a management keyword, because
  // some employees (e.g. Medvedev) have a canonical role like "team" but a
  // position string like "Project Manager" that signals they own delivery.
  // Artists, finance, designers, and externals are excluded.
  const fetchStaff = async () => {
    try {
      const res = await api.get("/employees");
      const all = res.data || [];
      const allowedRoles = new Set([
        "admin",
        "pm_lead",
        "pm",
        "sales_director",
        "sales",
        "crm_manager",
        "art_director",
      ]);
      const positionKeywords = [
        "manager",
        "lead",
        "director",
        "менедж",  // matches менеджер/менеджмент
        "проджект",
        "проект",
        "директор",
        "тимлід",
        "тимлид",
        "лід ",
      ];
      const pickable = all.filter((s) => {
        if (s.is_active === false) return false;
        const role = (s.role || "").toLowerCase().trim();
        if (role === "client" || role === "partner" || role === "artist" || role === "finance") return false;
        if (allowedRoles.has(role)) return true;
        const position = (s.position || "").toLowerCase();
        return positionKeywords.some((kw) => position.includes(kw));
      });
      pickable.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setStaffList(pickable);
    } catch (err) {
      console.error("Error fetching staff:", err);
    }
  };

  const fetchData = async () => {
    try {
      // For support, KPIs read from /support-services/stats (sales_services-based)
      // since the page no longer renders crm_payments rows for category=support.
      const statsRequest = category === "support"
        ? api.get("/support-services/stats", { params: { year: selectedYear } })
        : api.get("/crm/stats", { params: { year: selectedYear, category } });
      const [clientsRes, paymentsRes, statsRes, extraStatsRes, requestsRes, allClientsRes, tariffsRes, mgmtClientsRes] = await Promise.all([
        crmApi.getClients(true, category),
        crmApi.getPayments({ month: selectedMonth, category }),
        statsRequest,
        api.get("/crm/extra-stats", { params: { year: selectedYear, category } }),
        api.get("/client-requests").catch(() => ({ data: [] })),
        salesClientsApi.getAll("any").catch(() => ({ data: [] })),
        category === "support"
          ? api.get("/support-tariffs", { params: { active_only: true } }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
        // One-off charges may target ANY client, not only clients subscribed to
        // recurring products. Successively widened: support-only (backlog 0755a82e)
        // → crm+support → now the FULL client pool incl. sales clients, because a
        // one-time invoice is explicitly for clients NOT under ongoing support
        // (backlog 2736e3a5). Picker is searchable, so the larger list is fine.
        crmApi.getClients(true, "any").catch(() => ({ data: [] })),
      ]);
      setClients(clientsRes.data);
      setAllClients(allClientsRes.data || []);
      setManagementClients(mgmtClientsRes.data || []);
      setSupportTariffs(tariffsRes.data || []);
      setPayments(paymentsRes.data);
      setStats(statsRes.data);
      setExtraStats(extraStatsRes.data);
      
      // Mark CRM payments that already have payment requests sent
      const paymentRequests = (requestsRes.data || []).filter(
        r => r.request_type === "payment_request" && r.crm_payment_id
      );
      const sentPaymentIds = new Set(paymentRequests.map(r => r.crm_payment_id));
      setSentRequests(sentPaymentIds);
      setClientRequests(paymentRequests);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Quick status change for payments
  const handleQuickStatusChange = async (paymentId, newStatus) => {
    try {
      await crmApi.updatePayment(paymentId, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const result = await crmApi.importCsv();
      toast.success(`Imported ${result.data.clients_created} clients and ${result.data.payments_created} payments`);
      setImportDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import");
    } finally {
      setImporting(false);
    }
  };

  const handleStatusChange = async (paymentId, newStatus) => {
    try {
      await crmApi.updatePayment(paymentId, { status: newStatus });
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleActSigned = async (paymentId, signed) => {
    try {
      await crmApi.updatePayment(paymentId, { act_signed: signed });
      toast.success(signed ? "Act marked as signed" : "Act unmarked");
      fetchData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  // Send Payment Request to client.
  // Optimistic flip + backend dedup guarantee (see /api/client-requests POST
  // for the idempotent guard). Icon switches to CheckCircle2 immediately on
  // click so users can't accidentally send twice. 2026-05-22.
  const handleSendPaymentRequest = async (payment) => {
    const client = clients.find(c => c.id === payment.client_id);
    if (!client) {
      toast.error("Client not found");
      return;
    }
    if (sentRequests.has(payment.id)) {
      toast.info("Demand already sent for this payment");
      return;
    }

    const amount = payment.actual_amount || payment.planned_amount;
    const currency = payment.currency || "USD";

    // Optimistic flip — BEFORE await
    setSentRequests(prev => new Set([...prev, payment.id]));
    try {
      const res = await api.post("/client-requests", {
        client_id: payment.client_id,
        request_type: "payment_request",
        title: `Payment Request: ${client.name} - ${formatMonth(payment.month)}`,
        description: `Please process payment of ${amount.toLocaleString()} ${currency} for ${payment.payment_type === "monthly" ? "monthly subscription" : payment.payment_type}`,
        ball_in_court: "client",
        expected_action: "payment",
        payment_amount: amount,
        payment_currency: currency,
        payment_due_date: null,
        crm_payment_id: payment.id, // Link to CRM payment
      });
      const sender = res.data?.created_by_name || "you";
      const when = res.data?.created_at?.slice(0, 10) || "today";
      toast.success(`Demand sent by ${sender} on ${when}`);
    } catch (error) {
      // Rollback optimistic flip on failure
      setSentRequests(prev => {
        const next = new Set(prev);
        next.delete(payment.id);
        return next;
      });
      toast.error(error.response?.data?.detail || "Failed to send request");
    }
  };

  // Open invoice dialog (legacy — kept for the edge case where client.fop is empty)
  const openInvoiceDialog = (payment) => {
    setInvoicePayment(payment);
    setSelectedFopForInvoice("");
    setInvoiceCurrency(payment.currency || "USD");
    setInvoiceDialogOpen(true);
  };

  // Generate invoice directly using client.fop + client.invoice_currency.
  // Backend resolves both from the client when body omits fop_id/currency.
  // No fallback dialog — if FOP/currency missing, admin sets them on the
  // Clients tab (inline FOP picker + Edit Client → Invoice currency).
  // Per 2026-05-22 decision: invoice generation is one-click, never a modal.
  const handleGenerateInvoiceDirect = async (payment) => {
    const client = clients.find((c) => c.id === payment.client_id);
    if (!client?.fop) {
      toast.error(
        "FOP not set for this client. Open Clients tab → pick a FOP from the inline dropdown first."
      );
      return;
    }
    try {
      setGeneratingInvoice(true);
      toast.info("Generating invoice…");
      const response = await api.post(`/crm/payments/${payment.id}/generate-invoice`, {
        payment_id: payment.id,
        format: "docx",
        // fop_id + currency intentionally omitted — backend resolves from client
      });
      const { doc_base64, pdf_base64, invoice_number, amount_uah, exchange_rate, format: docFormat } = response.data;
      const fileData = doc_base64 || pdf_base64;
      // Trust the server-echoed format — doc_base64 always carries the bytes
      // (even for PDF, plus a pdf_base64 back-compat alias), so its mere
      // presence can't tell docx from pdf.
      const isDocx = docFormat ? docFormat === "docx" : !!doc_base64;
      const mimeType = isDocx
        ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        : "application/pdf";
      const ext = isDocx ? "docx" : "pdf";
      const blob = new Blob(
        [Uint8Array.from(atob(fileData), (c) => c.charCodeAt(0))],
        { type: mimeType }
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice_${invoice_number}.${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(
        amount_uah
          ? `Invoice ${invoice_number} — ₴${Number(amount_uah).toLocaleString()} @ ${exchange_rate}`
          : `Invoice ${invoice_number} downloaded`
      );
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Failed to generate invoice. Check that the client has FOP and invoice currency set."
      );
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Generate Рахунок (Invoice)
  const handleGenerateInvoice = async () => {
    if (!invoicePayment || !selectedFopForInvoice) {
      toast.error("Виберіть ФОП");
      return;
    }
    
    const currency = invoiceCurrency || invoicePayment.currency || "USD";
    
    try {
      setGeneratingInvoice(true);
      const response = await api.post(`/crm/payments/${invoicePayment.id}/generate-invoice`, {
        payment_id: invoicePayment.id,
        fop_id: selectedFopForInvoice,
        format: "docx",
        currency: currency
      });
      
      // Download DOCX
      const { doc_base64, pdf_base64, invoice_number, amount_uah, exchange_rate, currency, format: docFormat } = response.data;
      const fileData = doc_base64 || pdf_base64;
      // Trust the server-echoed format — doc_base64 always carries the bytes
      // (even for PDF, plus a pdf_base64 back-compat alias), so its mere
      // presence can't tell docx from pdf.
      const isDocx = docFormat ? docFormat === "docx" : !!doc_base64;
      const mimeType = isDocx ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "application/pdf";
      const ext = isDocx ? "docx" : "pdf";
      const blob = new Blob(
        [Uint8Array.from(atob(fileData), c => c.charCodeAt(0))],
        { type: mimeType }
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Рахунок_${invoice_number}.${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Рахунок ${invoice_number} створено. Сума: ${amount_uah.toLocaleString()} грн (курс ${exchange_rate})`);
      setInvoiceDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Помилка створення рахунку");
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Generate Акт
  const handleGenerateAct = async (payment) => {
    if (!payment.invoice_id) {
      toast.error("Спочатку створіть рахунок");
      return;
    }
    
    try {
      setGeneratingAct(true);
      const response = await api.post(`/crm/payments/${payment.id}/generate-act`);
      
      // Download DOCX
      const { doc_base64, pdf_base64, act_number, format: docFormat } = response.data;
      const fileData = doc_base64 || pdf_base64;
      // Trust the server-echoed format — doc_base64 always carries the bytes
      // (even for PDF, plus a pdf_base64 back-compat alias), so its mere
      // presence can't tell docx from pdf.
      const isDocx = docFormat ? docFormat === "docx" : !!doc_base64;
      const mimeType = isDocx ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "application/pdf";
      const ext = isDocx ? "docx" : "pdf";
      const blob = new Blob(
        [Uint8Array.from(atob(fileData), c => c.charCodeAt(0))],
        { type: mimeType }
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Акт_${act_number}.${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Акт ${act_number} створено`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Помилка створення акту");
    } finally {
      setGeneratingAct(false);
    }
  };

  // Bill-to (client legal entity / billing profile) picker — closes backlog f6d26cd9.
  const openBillToDialog = async (payment) => {
    setBillToPayment(payment);
    setBillToValue(payment.client_billing_profile_id || "default");
    setBillToClient(null);
    setBillToOpen(true);
    if (payment.client_id) {
      setBillToLoading(true);
      try {
        const res = await salesClientsApi.get(payment.client_id);
        setBillToClient(res.data || null);
      } catch (e) {
        console.warn("[crm-bill-to] failed to load client", e);
      } finally {
        setBillToLoading(false);
      }
    }
  };

  const handleSaveBillTo = async () => {
    if (!billToPayment) return;
    setBillToSaving(true);
    try {
      const profiles = billToClient?.billing_profiles || [];
      const profile = billToValue === "default"
        ? null
        : profiles.find((p) => p.id === billToValue);
      // Send empty string (not null) to clear: the PUT /crm/payments handler
      // drops None values, so "" is required to reset back to client default.
      await crmApi.updatePayment(billToPayment.id, {
        client_billing_profile_id: profile?.id || "",
        client_billing_profile_label: profile?.label || profile?.legal_name || "",
      });
      toast.success("Bill-to saved");
      setBillToOpen(false);
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => `${(d.loc || []).join(".")}: ${d.msg}`).join(" · ")
          : "Failed to save";
      toast.error(msg);
    } finally {
      setBillToSaving(false);
    }
  };

  // Download existing Рахунок (Invoice). format: "docx" (default) | "pdf"
  const handleDownloadInvoice = async (payment, format = "docx") => {
    if (!payment.invoice_number) {
      toast.error("Рахунок ще не створено");
      return;
    }

    try {
      setDownloadingInvoice(`${payment.id}:${format}`);
      const response = await api.get(`/crm/payments/${payment.id}/download-invoice?format=${format}`);
      
      // Download DOCX
      const { doc_base64, pdf_base64, invoice_number, format: docFormat } = response.data;
      const fileData = doc_base64 || pdf_base64;
      // Trust the server-echoed format — doc_base64 always carries the bytes
      // (even for PDF, plus a pdf_base64 back-compat alias), so its mere
      // presence can't tell docx from pdf.
      const isDocx = docFormat ? docFormat === "docx" : !!doc_base64;
      const mimeType = isDocx ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "application/pdf";
      const ext = isDocx ? "docx" : "pdf";
      const blob = new Blob(
        [Uint8Array.from(atob(fileData), c => c.charCodeAt(0))],
        { type: mimeType }
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Рахунок_${invoice_number}.${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Рахунок ${invoice_number} завантажено`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Помилка завантаження рахунку");
    } finally {
      setDownloadingInvoice(null);
    }
  };

  // Delete Invoice (allows recreation)
  const handleDeleteInvoice = async (payment) => {
    if (!payment.invoice_number) {
      toast.error("Немає рахунку для видалення");
      return;
    }
    
    if (!window.confirm(`Видалити рахунок ${payment.invoice_number}? Це дозволить створити новий.`)) {
      return;
    }
    
    try {
      setDeletingInvoice(payment.id);
      await api.delete(`/crm/payments/${payment.id}/invoice`);
      toast.success(`Рахунок ${payment.invoice_number} видалено`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Помилка видалення рахунку");
    } finally {
      setDeletingInvoice(null);
    }
  };

  // Download existing Акт (Act). format: "docx" (default) | "pdf"
  const handleDownloadAct = async (payment, format = "docx") => {
    if (!payment.act_number) {
      toast.error("Акт ще не створено");
      return;
    }

    try {
      setDownloadingAct(`${payment.id}:${format}`);
      const response = await api.get(`/crm/payments/${payment.id}/download-act?format=${format}`);
      
      // Download DOCX
      const { doc_base64, pdf_base64, act_number, format: docFormat } = response.data;
      const fileData = doc_base64 || pdf_base64;
      // Trust the server-echoed format — doc_base64 always carries the bytes
      // (even for PDF, plus a pdf_base64 back-compat alias), so its mere
      // presence can't tell docx from pdf.
      const isDocx = docFormat ? docFormat === "docx" : !!doc_base64;
      const mimeType = isDocx ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "application/pdf";
      const ext = isDocx ? "docx" : "pdf";
      const blob = new Blob(
        [Uint8Array.from(atob(fileData), c => c.charCodeAt(0))],
        { type: mimeType }
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Акт_${act_number}.${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Акт ${act_number} завантажено`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Помилка завантаження акту");
    } finally {
      setDownloadingAct(null);
    }
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split("-").map(Number);
    return `${MONTHS[month - 1]} ${year}`;
  };

  const navigateMonth = (direction) => {
    const [year, month] = selectedMonth.split("-").map(Number);
    let newMonth = month + direction;
    let newYear = year;
    if (newMonth > 12) { newMonth = 1; newYear++; }
    else if (newMonth < 1) { newMonth = 12; newYear--; }
    setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  // ============ CLIENT CRUD ============
  const openClientDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setClientForm({
        name: client.name || "",
        edrpou: client.edrpou || "",
        has_gpt: client.has_gpt || false,
        support_type: client.support_type || "none",
        support_price: client.support_price || 0,
        deployment_type: client.deployment_type || "cloud",
        price_per_user: client.price_per_user || 50,
        users_count: client.users_count ?? 0,
        notes: client.notes || "",
        currency: client.currency || "USD",
        fop: client.fop || "",
        selected_existing_id: null,
        support_tariff_id: client.support_tariff_id || null,
        support_sites: Array.isArray(client.support_sites) ? [...client.support_sites] : [],
        contract_url: client.contract_url || "",
        contract_status: client.contract_status || (client.contract_url ? "active" : "missing"),
        support_pm_id: client.support_pm_id || null,
        support_pm_name: client.support_pm_name || "",
        support_product_type: client.support_product_type || "standard",
        support_start_date: client.support_start_date || "",
        support_end_date: client.support_end_date || "",
        invoice_currency: client.invoice_currency || "EUR",
      });
    } else {
      setEditingClient(null);
      setClientForm({
        name: "",
        edrpou: "",
        has_gpt: false,
        support_type: "none",
        support_price: 0,
        deployment_type: "cloud",
        price_per_user: 50,
        users_count: 0,
        notes: "",
        currency: "USD",
        fop: "",
        selected_existing_id: null,
        support_tariff_id: null,
        support_sites: [],
        contract_url: "",
        contract_status: "active",
        support_pm_id: null,
        support_pm_name: "",
        support_product_type: "standard",
        support_start_date: "",
        support_end_date: "",
        invoice_currency: "EUR",
      });
    }
    setClientDialogOpen(true);
  };

  // When user picks an existing client from typeahead, fill the rest of the
  // form from that client's data so they can adjust before save.
  const applyExistingClient = (clientName, clientId) => {
    if (!clientId) {
      // Just typing — clear linkage and update name only.
      setClientForm((prev) => ({ ...prev, name: clientName, selected_existing_id: null }));
      return;
    }
    const existing = allClients.find((c) => c.id === clientId);
    if (!existing) return;
    setClientForm({
      name: existing.name || "",
      edrpou: existing.edrpou || "",
      has_gpt: existing.has_gpt || false,
      support_type: existing.support_type || "none",
      support_price: existing.support_price || 0,
      deployment_type: existing.deployment_type || "cloud",
      price_per_user: existing.price_per_user || 50,
      users_count: existing.users_count ?? 0,
      notes: existing.notes || "",
      currency: existing.currency || "USD",
      fop: existing.fop || "",
      selected_existing_id: clientId,
      support_tariff_id: existing.support_tariff_id || null,
      support_sites: Array.isArray(existing.support_sites) ? [...existing.support_sites] : [],
      contract_url: existing.contract_url || "",
      contract_status: existing.contract_status || (existing.contract_url ? "active" : "missing"),
      support_pm_id: existing.support_pm_id || null,
      support_pm_name: existing.support_pm_name || "",
      support_product_type: existing.support_product_type || "standard",
      support_start_date: existing.support_start_date || "",
      support_end_date: existing.support_end_date || "",
      invoice_currency: existing.invoice_currency || "EUR",
    });
  };

  const handleSaveClient = async () => {
    if (!clientForm.name.trim()) {
      toast.error("Company name is required");
      return;
    }
    if (isSupport && !clientForm.support_tariff_id) {
      toast.error("Support tariff is required — pick a tier from the Support Tariffs catalog.");
      return;
    }
    setSavingClient(true);
    try {
      // Sanitize support_sites — drop blank rows, ensure {url, label}
      const cleanedSites = (clientForm.support_sites || [])
        .map((s) => ({
          url: (s.url || "").trim(),
          label: (s.label || "").trim() || null,
        }))
        .filter((s) => s.url);
      const data = {
        ...clientForm,
        users_count: parseInt(clientForm.users_count) || 0,
        price_per_user: parseFloat(clientForm.price_per_user) || 0,
        support_price: parseFloat(clientForm.support_price) || 0,
        support_sites: cleanedSites,
        contract_url: (clientForm.contract_url || "").trim() || null,
        contract_status: clientForm.contract_status || null,
        support_pm_id: clientForm.support_pm_id || null,
        support_pm_name: clientForm.support_pm_name || null,
        invoice_currency: clientForm.invoice_currency || null,
      };
      // Remove form-only fields before send
      delete data.client_category;
      const pickedExistingId = data.selected_existing_id;
      delete data.selected_existing_id;

      let createdNew = false;
      let savedClientId = null;
      let savedClientName = data.name;
      if (editingClient) {
        const existingTags = editingClient.client_types || [editingClient.type].flat().filter(Boolean) || [];
        const merged = Array.from(new Set([...existingTags, category]));
        const res = await crmApi.updateClient(editingClient.id, { ...data, client_types: merged });
        savedClientId = res?.data?.id || editingClient.id;
        console.log("[support-save] updated existing client", savedClientId, res?.data);
        toast.success("Client updated");
      } else if (pickedExistingId) {
        const existing = allClients.find((c) => c.id === pickedExistingId);
        const existingTags = (existing?.client_types || [existing?.type].flat().filter(Boolean) || []);
        const merged = Array.from(new Set([...existingTags, category]));
        const res = await crmApi.updateClient(pickedExistingId, { ...data, client_types: merged });
        savedClientId = res?.data?.id || pickedExistingId;
        console.log("[support-save] attached", category, "tag to existing", savedClientId, res?.data);
        toast.success(
          existingTags.includes(category)
            ? "Client updated"
            : `Added ${category.toUpperCase()} contract to existing client`
        );
      } else {
        const res = await crmApi.createClient({ ...data, client_types: [category] });
        savedClientId = res?.data?.id;
        createdNew = true;
        console.log("[support-save] created new client", savedClientId, res?.data);
        toast.success(
          isSupport
            ? "Client created — next: click Generate Month to add this month's invoice."
            : "Client created"
        );
      }
      setClientDialogOpen(false);
      if (isSupport && (createdNew || pickedExistingId)) {
        setActiveTab("clients");
      }
      // Refetch and verify the saved client is in the new list. If not — loud
      // alert with both the saved id and the returned list so we can spot the
      // mismatch (most likely cause: backend wrote it under a different category
      // or filter, or POST succeeded but the GET filter excludes it).
      await fetchData();
      if (savedClientId && isSupport) {
        try {
          const verifyRes = await crmApi.getClients(true, category);
          const present = (verifyRes.data || []).some((c) => c.id === savedClientId);
          console.log(
            "[support-save] verify list contains",
            savedClientId,
            "→",
            present,
            "list size:",
            (verifyRes.data || []).length
          );
          if (!present) {
            toast.error(
              `"${savedClientName}" was saved (id ${savedClientId.slice(0, 8)}…) but doesn't appear in the Support list. Open DevTools console for details.`,
              { duration: 15000 }
            );
          }
        } catch (verr) {
          console.error("[support-save] verify failed", verr);
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg = "Failed to save client";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map((d) => `${(d.loc || []).join(".")}: ${d.msg}`).join(" · ");
      }
      console.error("[support-save] FAILED:", error.response?.status, error.response?.data, error);
      toast.error(msg, { duration: 15000 });
    } finally {
      setSavingClient(false);
    }
  };

  const handleDeleteClient = async (clientId, clientName) => {
    if (!window.confirm(`Delete client "${clientName}"? This will also delete all their payments.`)) {
      return;
    }
    try {
      await crmApi.deleteClient(clientId);
      toast.success("Client deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete client");
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, client, index) => {
    setDraggedClient({ client, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetIndex) => {
    e.preventDefault();
    if (!draggedClient || draggedClient.index === targetIndex) {
      setDraggedClient(null);
      return;
    }

    // Create new order array
    const newClients = [...clients];
    const [removed] = newClients.splice(draggedClient.index, 1);
    newClients.splice(targetIndex, 0, removed);

    // Update local state immediately for UI feedback
    setClients(newClients);
    setDraggedClient(null);

    // Update order on backend
    try {
      const order = newClients.map((c, idx) => ({ id: c.id, sort_order: idx }));
      await crmApi.reorderClients(order);
      toast.success("Order updated");
    } catch (error) {
      toast.error("Failed to save order");
      fetchData(); // Revert to server state
    }
  };

  // Drag-drop reorder for payment rows. We move within the FULL `payments`
  // array (by id, not display index) so the persisted order stays coherent even
  // when the view is filtered by client/status, then write per-payment sort_order.
  const handlePaymentDragStart = (e, payment) => {
    setDraggedPayment(payment);
    e.dataTransfer.effectAllowed = "move";
  };

  const handlePaymentDrop = async (e, targetPayment) => {
    e.preventDefault();
    if (!draggedPayment || draggedPayment.id === targetPayment.id) {
      setDraggedPayment(null);
      return;
    }
    const newPayments = [...payments];
    const from = newPayments.findIndex((p) => p.id === draggedPayment.id);
    const to = newPayments.findIndex((p) => p.id === targetPayment.id);
    if (from === -1 || to === -1) {
      setDraggedPayment(null);
      return;
    }
    const [removed] = newPayments.splice(from, 1);
    newPayments.splice(to, 0, removed);
    setPayments(newPayments);
    setDraggedPayment(null);

    try {
      const order = newPayments.map((p, idx) => ({ id: p.id, sort_order: idx }));
      await crmApi.reorderPayments(order);
      toast.success("Order updated");
    } catch (error) {
      toast.error("Failed to save order");
      fetchData(); // Revert to server state
    }
  };

  // ============ PAYMENT CRUD ============
  const openPaymentDialog = (payment = null) => {
    if (payment) {
      setEditingPayment(payment);
      setPaymentForm({
        client_id: payment.client_id || "",
        month: payment.month || selectedMonth,
        payment_type: payment.payment_type || "monthly",
        planned_amount: payment.planned_amount || 0,
        actual_amount: payment.actual_amount || null,
        currency: payment.currency || "USD",
        status: payment.status || "planned",
        act_signed: payment.act_signed || false,
        services_description: payment.services_description || "",
      });
    } else {
      setEditingPayment(null);
      setPaymentForm({
        client_id: "",
        month: selectedMonth,
        payment_type: "monthly",
        planned_amount: 0,
        actual_amount: null,
        currency: "USD",
        status: "planned",
        act_signed: false,
        services_description: "",
      });
    }
    setPaymentDialogOpen(true);
  };

  // Shortcut: open the Add Payment dialog pre-set to "extra" (one-off custom
  // development charge). Mirrors the Support "Add One-off" button — the
  // existing payment dialog already supports type="extra", we just default
  // to it so admins don't have to remember to change the dropdown.
  const openOneOffPayment = () => {
    setEditingPayment(null);
    setPaymentForm({
      client_id: "",
      month: selectedMonth,
      payment_type: "extra",
      planned_amount: 0,
      actual_amount: null,
      currency: "USD",
      status: "planned",
      act_signed: false,
      services_description: "",
    });
    setPaymentDialogOpen(true);
  };

  const handleSavePayment = async () => {
    if (!paymentForm.client_id) {
      toast.error("Please select a client");
      return;
    }
    setSavingPayment(true);
    try {
      const data = {
        ...paymentForm,
        planned_amount: parseFloat(paymentForm.planned_amount) || 0,
        actual_amount: paymentForm.actual_amount ? parseFloat(paymentForm.actual_amount) : null,
        category,
      };
      if (editingPayment) {
        await crmApi.updatePayment(editingPayment.id, data);
        toast.success("Payment updated");
      } else {
        await crmApi.createPayment(data);
        toast.success("Payment created");
      }
      setPaymentDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save payment");
    } finally {
      setSavingPayment(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm("Delete this payment?")) {
      return;
    }
    try {
      await crmApi.deletePayment(paymentId);
      toast.success("Payment deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete payment");
    }
  };

  const handleGenerateMonthPayments = async () => {
    if (!window.confirm(`Generate payments for all active clients for ${formatMonth(selectedMonth)}?`)) {
      return;
    }
    setGeneratingPayments(true);
    try {
      const result = await crmApi.generateMonthPayments(selectedMonth, category);
      toast.success(`Created ${result.data.payments_created} payments (${result.data.payments_skipped} skipped)`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate payments");
    } finally {
      setGeneratingPayments(false);
    }
  };

  // Bank transaction linking functions
  const handleOpenLinkTransaction = async (payment) => {
    setLinkingPaymentId(payment.id);
    setLoadingTransactions(true);
    setLinkTransactionDialogOpen(true);
    
    try {
      // Picker filters by payer name / ЕДРПОУ of this payment's client
      // (matchable endpoint), so a manager sees only plausibly-matching
      // transactions instead of the whole unmatched pool. Also accessible
      // to crm_manager, unlike the broad /bank-transactions/unmatched.
      const response = await api.get(
        `/bank-transactions/matchable?payment_type=crm&payment_id=${payment.id}`
      );
      setUnmatchedTransactions(response.data.items || []);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast.error("Не удалось загрузить транзакции");
      setUnmatchedTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleLinkTransaction = async (transactionId) => {
    if (!linkingPaymentId) return;
    
    try {
      await api.post(`/crm/payments/${linkingPaymentId}/link-transaction?transaction_id=${transactionId}`);
      toast.success("Платёж связан с банковской транзакцией");
      setLinkTransactionDialogOpen(false);
      setLinkingPaymentId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка при связывании");
    }
  };

  const handleUnlinkTransaction = async (paymentId) => {
    try {
      await api.delete(`/crm/payments/${paymentId}/link-transaction`);
      toast.success("Связь с транзакцией удалена");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка при удалении связи");
    }
  };

  // Get client by ID for displaying users in payments table
  const getClientById = (clientId) => clients.find(c => c.id === clientId);

  // Filter payments
  const filteredPayments = useMemo(() => {
    return payments.filter(p => {
      // "unpaid" is not a status but a question — everything still owed this
      // month, i.e. planned + invoiced. backlog 00556a59.
      if (filterStatus === "unpaid") {
        if (p.status === "paid" || p.status === "cancelled") return false;
      } else if (filterStatus !== "all" && p.status !== filterStatus) return false;
      if (filterClient !== "all" && p.client_id !== filterClient) return false;
      return true;
    });
  }, [payments, filterStatus, filterClient]);

  // How many companies still owe for the selected month. Counted off the
  // month's payments (not the filtered view), so it answers "who hasn't paid
  // in September" regardless of what the table is filtered to, and follows the
  // month switcher for free. backlog 00556a59.
  const unpaidClientCount = useMemo(() => {
    const owing = new Set();
    for (const p of payments) {
      if (p.status === "paid" || p.status === "cancelled") continue;
      owing.add(p.client_id || p.client_name || p.id);
    }
    return owing.size;
  }, [payments]);

  // Calculate month stats
  // For Support (sales_services-based), totals come straight from
  // /support-services/stats.monthly_breakdown — already FX-normalized to USD
  // on backend, so KPI cards show real magnitude. For legacy CRM (crm_payments-
  // based) we calculate locally from the loaded payments collection.
  // Both CRM and Support read monthly totals from /crm/stats.monthly_breakdown
  // (or /support-services/stats for support). These are FX-normalized to USD
  // on the backend so mixed-currency sums display correctly. Local reduce on
  // `payments` is a last-resort fallback for very old responses.
  const monthStats = useMemo(() => {
    const row = stats?.monthly_breakdown?.find((r) => r.month === selectedMonth);
    if (row) {
      return {
        planned: row.planned || 0,
        actual: row.actual || 0,
        paid: row.paid || 0,
        invoiced: row.invoiced || 0,
        pending: row.pending || 0,
        commissionEarned: row.commission_earned || 0,
        commissionExpected: row.commission_expected || 0,
      };
    }
    if (isSupport) {
      return { planned: 0, actual: 0, paid: 0, invoiced: 0, pending: 0, commissionEarned: 0, commissionExpected: 0 };
    }
    return filteredPayments.reduce(
      (acc, p) => {
        acc.planned += p.planned_amount || 0;
        acc.actual += p.actual_amount || 0;
        if (p.status === "paid") acc.paid += p.actual_amount || p.planned_amount || 0;
        else if (p.status === "invoiced") acc.invoiced += p.planned_amount || 0;
        else acc.pending += p.planned_amount || 0;
        return acc;
      },
      { planned: 0, actual: 0, paid: 0, invoiced: 0, pending: 0 }
    );
  }, [filteredPayments, isSupport, stats, selectedMonth]);

  const clearFilters = () => {
    setFilterStatus("all");
    setFilterClient("all");
  };

  const hasActiveFilters = filterStatus !== "all" || filterClient !== "all";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  // Clients table extracted so the Support "Warranty" / "Hosting" tabs can
  // reuse it with a filtered list (backlog d9bc5c5b). Drag-reorder only on the
  // full "Clients" tab (allowDrag) — reordering a filtered subset would corrupt
  // sort_order.
  const renderClientsCard = (list, allowDrag = false) => (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <CardContent className="p-0">
        {list.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            <Building2 className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p>No clients here</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-400 font-medium w-10">#</TableHead>
                <TableHead className="text-zinc-400 font-medium">Client</TableHead>
                {!isSupport && <TableHead className="text-zinc-400 font-medium text-center">Users</TableHead>}
                {!isSupport && <TableHead className="text-zinc-400 font-medium text-center">GPT</TableHead>}
                {isSupport && <TableHead className="text-zinc-400 font-medium">Sites</TableHead>}
                <TableHead className="text-zinc-400 font-medium">Support</TableHead>
                {/* Contract — shown for CRM clients too, not just Support: managers
                    need to see at a glance who has a signed agreement on file and
                    open it in one click. backlog 604c214e */}
                <TableHead className="text-zinc-400 font-medium">Contract</TableHead>
                {isSupport && <TableHead className="text-zinc-400 font-medium">PM</TableHead>}
                {!isSupport && <TableHead className="text-zinc-400 font-medium">Type</TableHead>}
                <TableHead className="text-zinc-400 font-medium">FOP</TableHead>
                <TableHead className="text-zinc-400 font-medium text-right">Monthly</TableHead>
                <TableHead className="w-20 sticky right-0 z-20 bg-zinc-900 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.8)]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((client, index) => {
                const tariff = isSupport && client.support_tariff_id
                  ? supportTariffs.find((t) => t.id === client.support_tariff_id)
                  : null;
                const monthly = isSupport
                  ? (tariff ? Number(tariff.price) || 0 : Number(client.support_price) || 0)
                  : ((client.users_count * client.price_per_user) + (client.support_price || 0));
                const monthlyCurrency = isSupport && tariff
                  ? (tariff.currency || client.currency || "EUR")
                  : (client.currency || "USD");
                const currencySymbol = monthlyCurrency === "EUR" ? "€" : monthlyCurrency === "UAH" ? "₴" : "$";
                return (
                  <TableRow
                    key={client.id}
                    className={cn(
                      "border-zinc-800 hover:bg-zinc-800/50",
                      allowDrag && "cursor-grab",
                      draggedClient?.client.id === client.id && "opacity-50"
                    )}
                    draggable={allowDrag}
                    onDragStart={allowDrag ? (e) => handleDragStart(e, client, index) : undefined}
                    onDragOver={allowDrag ? (e) => handleDragOver(e, index) : undefined}
                    onDrop={allowDrag ? (e) => handleDrop(e, index) : undefined}
                  >
                    <TableCell className="w-10">
                      <div className="flex items-center gap-1">
                        {allowDrag && <GripVertical className="h-4 w-4 text-zinc-600 cursor-grab" />}
                        <span className="text-zinc-500 text-xs font-mono">{index + 1}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {client.currency === "EUR" && (
                          <Euro className="h-3 w-3 text-blue-400" />
                        )}
                        <div>
                          <p className="text-zinc-50 font-medium text-sm">{client.name}</p>
                          {client.edrpou && <p className="text-xs text-zinc-500">ЄДРПОУ: {client.edrpou}</p>}
                          {isSupport && (client.support_start_date || client.support_end_date) && (() => {
                            const st = periodStatus(client);
                            return (
                              <p className={cn(
                                "text-[11px] mt-0.5 inline-flex items-center gap-1",
                                st === "expired" ? "text-red-400" : st === "soon" ? "text-amber-400" : "text-zinc-500"
                              )}>
                                <Calendar className="h-3 w-3" />
                                {client.support_start_date || "—"} → {client.support_end_date || "—"}
                                {st === "expired" && <span className="font-semibold">· Expired</span>}
                                {st === "soon" && <span className="font-semibold">· Ends soon</span>}
                              </p>
                            );
                          })()}
                        </div>
                      </div>
                    </TableCell>
                    {!isSupport && (
                      <TableCell className="text-center">
                        <span className="font-mono text-zinc-50">{client.users_count}</span>
                      </TableCell>
                    )}
                    {!isSupport && (
                      <TableCell className="text-center">
                        {client.has_gpt ? (
                          <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/20">GPT</Badge>
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )}
                      </TableCell>
                    )}
                    {isSupport && (
                      <TableCell>
                        {(client.support_sites && client.support_sites.length > 0) ? (
                          <div className="flex flex-wrap items-center gap-1 max-w-[280px]">
                            {client.support_sites.slice(0, 2).map((site, idx) => (
                              <a
                                key={idx}
                                href={site.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs text-sky-400 hover:text-sky-300 hover:underline bg-sky-500/10 border border-sky-500/20 rounded px-1.5 py-0.5 max-w-[160px] truncate"
                                title={site.url}
                              >
                                {site.label || site.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                              </a>
                            ))}
                            {client.support_sites.length > 2 && (
                              <span className="text-xs text-zinc-500">+{client.support_sites.length - 2}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-amber-500/70 italic">No sites listed</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {isSupport ? (
                        <span className="text-sm text-zinc-300">
                          {client.support_tariff_id ? (
                            <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                              {(supportTariffs.find((t) => t.id === client.support_tariff_id) || {}).name || "Tariff"}
                            </Badge>
                          ) : client.support_price ? (
                            <span className="text-zinc-400">Custom ({currencySymbol}{client.support_price})</span>
                          ) : (
                            <span className="text-zinc-600">—</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-sm text-zinc-400">
                          {client.support_type === "none" ? "No support" :
                           client.support_type === "premium" ? `Premium (${currencySymbol}${client.support_price})` : "Basic"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {client.contract_url ? (
                        <a
                          href={client.contract_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className={cn(
                            "text-xs hover:underline inline-flex items-center gap-1",
                            client.contract_status === "needs_renewal" ? "text-amber-400" : "text-emerald-400"
                          )}
                        >
                          <FileText className="h-3 w-3" />
                          {client.contract_status === "needs_renewal" ? "Renew" : "View"}
                        </a>
                      ) : (
                        <span className="text-xs text-amber-500/70 italic inline-flex items-center gap-1">
                          <X className="h-3 w-3" /> Missing
                        </span>
                      )}
                    </TableCell>
                    {isSupport && (
                      <TableCell>
                        {client.support_pm_name ? (
                          <span className="text-xs text-zinc-300">{client.support_pm_name}</span>
                        ) : (
                          <span className="text-xs text-zinc-600">—</span>
                        )}
                      </TableCell>
                    )}
                    {!isSupport && (
                      <TableCell>
                        <Badge variant="outline" className={cn(
                          "text-xs",
                          client.deployment_type === "cloud"
                            ? "bg-sky-500/10 text-sky-400 border-sky-500/20"
                            : "bg-zinc-700 text-zinc-300 border-zinc-600"
                        )}>
                          {client.deployment_type === "cloud" ? "Cloud" : "Box"}
                        </Badge>
                      </TableCell>
                    )}
                    <TableCell>
                      {/* Inline FOP picker — saves to client immediately so admin
                          doesn't have to open Edit dialog just to assign a FOP. */}
                      <Select
                        value={client.fop || "__none__"}
                        onValueChange={async (v) => {
                          const newFop = v === "__none__" ? "" : v;
                          if (newFop === (client.fop || "")) return;
                          try {
                            await crmApi.updateClient(client.id, { fop: newFop });
                            toast.success(newFop ? `FOP set to ${newFop}` : "FOP cleared");
                            fetchData();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Failed to update FOP");
                          }
                        }}
                      >
                        <SelectTrigger
                          className={cn(
                            "h-7 text-xs border-0 w-[150px] bg-transparent hover:bg-zinc-800",
                            client.fop ? "text-zinc-300" : "text-amber-500/70 italic"
                          )}
                        >
                          <SelectValue placeholder="Pick FOP" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-700 max-h-72">
                          <SelectItem value="__none__" className="text-zinc-500">— Not set —</SelectItem>
                          {fops.filter((f) => f.is_active !== false).map((f) => (
                            <SelectItem key={f.id} value={f.name} className="text-zinc-50">
                              {f.name}
                            </SelectItem>
                          ))}
                          {fops.length === 0 && (
                            <SelectItem value="__no-fops__" disabled className="text-zinc-500">
                              No FOPs configured (add in Settings)
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono text-green-400 font-medium">
                        {currencySymbol}{monthly}
                      </span>
                    </TableCell>
                    <TableCell className="sticky right-0 z-10 bg-zinc-900 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.8)]">
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openClientDialog(client)}
                          className="text-zinc-400 hover:text-zinc-50 h-7 px-2"
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteClient(client.id, client.name)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );

  // Support product groupings for the Warranty / Hosting tabs.
  const warrantyClients = isSupport ? clients.filter((c) => c.support_product_type === "warranty") : [];
  const hostingClients = isSupport ? clients.filter((c) => c.support_product_type === "hosting") : [];

  // Service-period status for warranty/hosting clients (backlog ad55e8ea):
  // "expired" once support_end_date passes, "soon" within 14 days, else "ok".
  const todayIso = new Date().toISOString().slice(0, 10);
  const periodStatus = (client) => {
    const end = client.support_end_date;
    if (!end) return null;
    if (end < todayIso) return "expired";
    const days = Math.round((new Date(end) - new Date(todayIso)) / 86400000);
    return days <= 14 ? "soon" : "ok";
  };
  const warrantyExpired = warrantyClients.some((c) => periodStatus(c) === "expired");
  const hostingExpired = hostingClients.some((c) => periodStatus(c) === "expired");

  return (
    <div data-testid="crm-income-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">{pageTitle}</h1>
          <p className="text-sm text-zinc-500">{pageSubtitle}</p>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => openClientDialog()} className="border-zinc-700 text-zinc-300">
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
          {/* Add Payment / Import CSV operate on the legacy crm_payments collection.
              For Support category we drive everything through sales_services + Generate Month,
              so these buttons would write to the wrong place — hide them. */}
          {!isSupport && (
            <Button variant="outline" onClick={() => openPaymentDialog()} className="border-zinc-700 text-zinc-300">
              <Plus className="h-4 w-4 mr-2" />
              Add Payment
            </Button>
          )}
          {/* Add One-off — shortcut to the Add Payment dialog with payment_type="extra"
              pre-selected, for custom development / ad-hoc charges. */}
          {!isSupport && (
            <Button
              variant="outline"
              onClick={openOneOffPayment}
              className="border-amber-700/50 text-amber-300 hover:bg-amber-500/10"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add One-off
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleGenerateMonthPayments}
            disabled={generatingPayments}
            className="border-green-700 text-green-400 hover:bg-green-900/20"
          >
            <Zap className="h-4 w-4 mr-2" />
            {generatingPayments ? "Generating..." : "Generate Month"}
          </Button>
          {!isSupport && (
            <Button variant="outline" onClick={() => setImportDialogOpen(true)} className="border-zinc-700 text-zinc-300">
              <Upload className="h-4 w-4 mr-2" />
              Import CSV
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className={`grid grid-cols-2 ${(stats?.my_bonus_percent || 0) > 0 ? "md:grid-cols-6" : "md:grid-cols-5"} gap-3 mb-6`}>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-3">
            <p className="text-xs text-zinc-500 uppercase font-mono mb-1">Clients</p>
            <div className="flex items-baseline gap-2">
              <p className="text-xl font-bold text-zinc-50 font-mono">{clients.length}</p>
              {/* Not paid this month — click to see exactly who. */}
              <button
                type="button"
                onClick={() => setFilterStatus(filterStatus === "unpaid" ? "all" : "unpaid")}
                title={unpaidClientCount ? "Show the companies that haven't paid this month" : "Everyone has paid this month"}
                data-testid="crm-unpaid-clients"
                className={cn(
                  "font-mono text-sm rounded px-1.5 py-0.5 transition-colors",
                  unpaidClientCount
                    ? "text-amber-400 hover:bg-amber-500/10"
                    : "text-emerald-500/80 hover:bg-emerald-500/10",
                  filterStatus === "unpaid" && "bg-amber-500/15 ring-1 ring-amber-500/40"
                )}
              >
                {unpaidClientCount} unpaid
              </button>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-3">
            <p className="text-xs text-zinc-500 uppercase font-mono mb-1">Plan</p>
            <p className="text-xl font-bold text-sky-400 font-mono">{formatAmount(monthStats.planned, "USD")}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-3">
            <p className="text-xs text-zinc-500 uppercase font-mono mb-1">Invoiced</p>
            <p className="text-xl font-bold text-orange-400 font-mono">{formatAmount(monthStats.invoiced, "USD")}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-3">
            <p className="text-xs text-zinc-500 uppercase font-mono mb-1">Paid</p>
            <p className="text-xl font-bold text-green-400 font-mono">{formatAmount(monthStats.paid, "USD")}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-3">
            <p className="text-xs text-zinc-500 uppercase font-mono mb-1">Pending</p>
            <p className="text-xl font-bold text-zinc-400 font-mono">{formatAmount(monthStats.pending, "USD")}</p>
          </CardContent>
        </Card>
        {(stats?.my_bonus_percent || 0) > 0 && (
          <Card className="bg-amber-950/20 border-amber-800/40">
            <CardContent className="p-3">
              <p className="text-xs text-zinc-500 uppercase font-mono mb-1">
                My commission <span className="text-amber-500/80 normal-case">{stats.my_bonus_percent}%</span>
              </p>
              <p className="text-xl font-bold text-amber-400 font-mono">{formatAmount(monthStats.commissionEarned, "USD")}</p>
              <p className="text-[10px] text-zinc-500 font-mono mt-0.5">{formatAmount(monthStats.commissionExpected, "USD")} expected</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="payments" className="data-[state=active]:bg-zinc-800">
            <DollarSign className="h-4 w-4 mr-1" /> Payments
          </TabsTrigger>
          <TabsTrigger value="clients" className="data-[state=active]:bg-zinc-800">
            <Building2 className="h-4 w-4 mr-1" /> Clients ({clients.length})
          </TabsTrigger>
          {isSupport && (
            <TabsTrigger value="warranty" className={cn("data-[state=active]:bg-zinc-800", warrantyExpired && "text-red-400")}>
              {warrantyExpired ? <AlertTriangle className="h-4 w-4 mr-1" /> : <ShieldCheck className="h-4 w-4 mr-1" />}
              Warranty ({warrantyClients.length})
            </TabsTrigger>
          )}
          {isSupport && (
            <TabsTrigger value="hosting" className={cn("data-[state=active]:bg-zinc-800", hostingExpired && "text-red-400")}>
              {hostingExpired ? <AlertTriangle className="h-4 w-4 mr-1" /> : <Server className="h-4 w-4 mr-1" />}
              Hosting ({hostingClients.length})
            </TabsTrigger>
          )}
          <TabsTrigger value="yearly" className="data-[state=active]:bg-zinc-800">
            <Calendar className="h-4 w-4 mr-1" /> Year View
          </TabsTrigger>
        </TabsList>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4">
          {isSupport ? (
            <SupportPaymentsView
              selectedMonth={selectedMonth}
              onNavigateMonth={navigateMonth}
              onGenerateMonth={async () => {
                try {
                  const result = await crmApi.generateMonthPayments(selectedMonth, "support");
                  toast.success(
                    `Created ${result.data.payments_created} payments (${result.data.payments_skipped} skipped)`
                  );
                  fetchData();
                } catch (error) {
                  toast.error(error.response?.data?.detail || "Failed to generate payments");
                }
              }}
              supportClients={clients}
              oneOffClients={managementClients.length ? managementClients : clients}
              supportTariffs={supportTariffs}
              onAfterOneOff={fetchData}
            />
          ) : (
          <>
          {/* Month Navigation & Filters */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigateMonth(-1)} className="text-zinc-400 hover:text-zinc-50">
                <ChevronLeft className="h-4 w-4 mr-1" /> Previous
              </Button>
              <h2 className="text-lg font-semibold text-zinc-50 font-['Manrope'] min-w-[120px] text-center">
                {formatMonth(selectedMonth)}
              </h2>
              <Button variant="ghost" onClick={() => navigateMonth(1)} className="text-zinc-400 hover:text-zinc-50">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            <div className="flex items-center gap-3">
              <Filter className="h-4 w-4 text-zinc-500" />
              <Select value={filterClient} onValueChange={setFilterClient}>
                <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800 text-zinc-50 h-9">
                  <SelectValue placeholder="Client" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                  <SelectItem value="all" className="text-zinc-50">All Clients</SelectItem>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id} className="text-zinc-50">{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[130px] bg-zinc-900 border-zinc-800 text-zinc-50 h-9">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all" className="text-zinc-50">All Status</SelectItem>
                  <SelectItem value="unpaid" className="text-amber-400">Unpaid</SelectItem>
                  <SelectItem value="planned" className="text-zinc-50">Planned</SelectItem>
                  <SelectItem value="invoiced" className="text-zinc-50">Invoiced</SelectItem>
                  <SelectItem value="paid" className="text-zinc-50">Paid</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-zinc-400 hover:text-zinc-50 h-9">
                  <X className="h-4 w-4 mr-1" /> Clear
                </Button>
              )}
            </div>
          </div>

          {/* Payments Table */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-0">
              {filteredPayments.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <DollarSign className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>No payments for this month</p>
                  <p className="text-xs text-zinc-600 mt-1">Click "Generate Month" to create payments from clients</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-400 font-medium w-10 text-center">#</TableHead>
                      <TableHead className="text-zinc-400 font-medium">Client</TableHead>
                      {!isSupport && <TableHead className="text-zinc-400 font-medium text-center">Users</TableHead>}
                      {isSupport && <TableHead className="text-zinc-400 font-medium">Sites</TableHead>}
                      {/* Contract on the payment row — the client's agreement, one
                          click away while working the invoice. backlog 604c214e */}
                      <TableHead className="text-zinc-400 font-medium">Contract</TableHead>
                      <TableHead className="text-zinc-400 font-medium">Type</TableHead>
                      <TableHead className="text-zinc-400 font-medium text-right">Plan</TableHead>
                      <TableHead className="text-zinc-400 font-medium">FOP</TableHead>
                      <TableHead className="text-zinc-400 font-medium">Inv. ccy</TableHead>
                      <TableHead className="text-zinc-400 font-medium text-right">UAH</TableHead>
                      <TableHead className="text-zinc-400 font-medium text-right">Actual</TableHead>
                      {/* Status is pinned next to Actions. Both were free-floating
                          before, so on a wide row the pinned Actions block simply
                          sat on top of the Status select and swallowed it.
                          backlog cf9c01ef */}
                      <TableHead className="text-zinc-400 font-medium text-center w-[160px] sticky right-[304px] z-20 bg-zinc-900">Status</TableHead>
                      {/* Actions pinned to the right edge — with long client names
                          the table scrolls horizontally and the Delete icon used to
                          disappear off-screen. backlog 85dae412 */}
                      <TableHead className="text-zinc-400 font-medium w-[304px] min-w-[304px] text-right sticky right-0 z-20 bg-zinc-900 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.8)]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPayments.map((payment, index) => {
                      const client = getClientById(payment.client_id);
                      const currency = payment.currency || client?.currency || "USD";
                      const currencySymbol = currency === "EUR" ? "€" : currency === "UAH" ? "₴" : "$";
                      return (
                        <TableRow
                          key={payment.id}
                          className={cn(
                            "border-zinc-800 hover:bg-zinc-800/50",
                            draggedPayment?.id === payment.id && "opacity-50"
                          )}
                          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                          onDrop={(e) => handlePaymentDrop(e, payment)}
                        >
                          <TableCell className="w-10">
                            <div
                              className="flex items-center gap-1 cursor-grab active:cursor-grabbing"
                              draggable
                              onDragStart={(e) => handlePaymentDragStart(e, payment)}
                              title="Drag to reorder"
                            >
                              <GripVertical className="h-4 w-4 text-zinc-600" />
                              <span className="text-zinc-500 text-xs font-mono">{index + 1}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {currency === "EUR" && <Euro className="h-3 w-3 text-blue-400" />}
                              <span className="text-zinc-50 font-medium text-sm">{payment.client_name}</span>
                              {client?.full_name && (
                                <TooltipProvider>
                                  <Tooltip delayDuration={200}>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-zinc-500 hover:text-zinc-300">
                                        <Eye className="h-3 w-3" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-xs bg-zinc-800 border-zinc-700 text-zinc-200">
                                      <pre className="text-xs whitespace-pre-wrap">{client.full_name}</pre>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          </TableCell>
                          {!isSupport && (
                            <TableCell className="text-center">
                              <span className="font-mono text-zinc-400">{client?.users_count || "—"}</span>
                            </TableCell>
                          )}
                          {isSupport && (
                            <TableCell>
                              {(client?.support_sites && client.support_sites.length > 0) ? (
                                <div className="flex flex-wrap items-center gap-1 max-w-[260px]">
                                  {client.support_sites.slice(0, 2).map((site, idx) => (
                                    <a
                                      key={idx}
                                      href={site.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="text-xs text-sky-400 hover:text-sky-300 hover:underline bg-sky-500/10 border border-sky-500/20 rounded px-1.5 py-0.5 max-w-[140px] truncate"
                                      title={site.url}
                                    >
                                      {site.label || site.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                                    </a>
                                  ))}
                                  {client.support_sites.length > 2 && (
                                    <span className="text-xs text-zinc-500">+{client.support_sites.length - 2}</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-zinc-600">—</span>
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            {client?.contract_url ? (
                              <a
                                href={client.contract_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={cn(
                                  "text-xs hover:underline inline-flex items-center gap-1",
                                  client.contract_status === "needs_renewal" ? "text-amber-400" : "text-emerald-400"
                                )}
                                title={client.contract_url}
                              >
                                <FileText className="h-3 w-3" />
                                {client.contract_status === "needs_renewal" ? "Renew" : "View"}
                              </a>
                            ) : (
                              <span className="text-xs text-amber-500/70 italic inline-flex items-center gap-1">
                                <X className="h-3 w-3" /> Missing
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-zinc-800 text-zinc-300 border-zinc-700 text-xs">
                              {payment.payment_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="font-mono text-emerald-300 cursor-help">
                                    {formatAmount(payment.planned_amount || 0, currency)}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="bg-zinc-800 border-zinc-700 text-xs">
                                  <p>Saved as {currencySymbol}{(payment.planned_amount || 0).toLocaleString()} ({currency})</p>
                                  <p className="text-zinc-500 mt-1">Invoice DOCX uses the saved value.</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                          {/* FOP — from client.fop (set via Edit Client) */}
                          <TableCell>
                            {client?.fop ? (
                              <span className="inline-flex items-center gap-1 text-xs text-zinc-300">
                                <Building2 className="h-3 w-3 text-sky-400" />
                                {client.fop}
                              </span>
                            ) : (
                              <span className="text-xs text-amber-500/70 italic">Not set</span>
                            )}
                          </TableCell>
                          {/* Invoice currency — drives invoice DOCX template */}
                          <TableCell>
                            <Badge className="bg-zinc-700/40 text-zinc-300 border-zinc-700 text-xs">
                              {currency}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {payment.invoice_amount_uah ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <span className="font-mono text-green-400">₴{payment.invoice_amount_uah?.toLocaleString()}</span>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-zinc-800 border-zinc-700">
                                    <p className="text-xs">Курс: {payment.invoice_exchange_rate}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <span className="text-zinc-600 text-xs">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="font-mono text-zinc-50">
                              {payment.actual_amount ? formatAmount(payment.actual_amount, currency) : "—"}
                            </span>
                          </TableCell>
                          <TableCell className="text-center w-[160px] sticky right-[304px] z-10 bg-zinc-900">
                            <div className="flex items-center justify-center gap-1">
                              <Select
                                value={payment.status}
                                onValueChange={(v) => handleQuickStatusChange(payment.id, v)}
                              >
                                <SelectTrigger className={cn(
                                  "h-7 text-xs border-0 w-[110px]",
                                  payment.status === "paid" ? "bg-green-500/10 text-green-400" :
                                  payment.status === "invoiced" ? "bg-orange-500/10 text-orange-400" :
                                  payment.status === "cancelled" ? "bg-red-500/10 text-red-400" :
                                  "bg-zinc-700/50 text-zinc-400"
                                )}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                  <SelectItem value="planned" className="text-zinc-300">
                                    <div className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />Planned
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="invoiced" className="text-orange-400">
                                    <div className="flex items-center gap-1">
                                      <FileText className="h-3 w-3" />Invoiced
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="paid" className="text-green-400">
                                    <div className="flex items-center gap-1">
                                      <CheckCircle2 className="h-3 w-3" />Paid
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="cancelled" className="text-red-400">
                                    <div className="flex items-center gap-1">
                                      <XCircle className="h-3 w-3" />Cancelled
                                    </div>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              {/* "Paid from bank" indicator when bank_transaction is linked.
                                  Shows date + counterparty + auto-matched flag in tooltip. */}
                              {payment.bank_transaction_id && payment.bank_transaction_info && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex items-center text-emerald-400 cursor-help shrink-0">
                                        <Landmark className="h-3.5 w-3.5" />
                                        {payment.bank_transaction_info.auto_matched && (
                                          <span className="text-emerald-500/70 text-[9px] ml-0.5">auto</span>
                                        )}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-zinc-800 border-zinc-700 text-xs max-w-xs">
                                      <p className="font-semibold text-emerald-300">
                                        {payment.bank_transaction_info.amount?.toLocaleString()} {payment.bank_transaction_info.currency}
                                      </p>
                                      <p>Date: {payment.bank_transaction_info.date}</p>
                                      {payment.bank_transaction_info.counterparty_name && (
                                        <p>From: {payment.bank_transaction_info.counterparty_name}</p>
                                      )}
                                      {payment.bank_transaction_info.auto_matched && (
                                        <p className="text-emerald-400 mt-1">Auto-matched by invoice number</p>
                                      )}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          </TableCell>
                          {/* Compact icon-only actions: Invoice / Act / Demand /
                              Bank Tx / Edit / Delete — mirrors Support layout.
                              Pinned right so Delete stays reachable no matter how
                              wide the row grows. backlog 85dae412 */}
                          <TableCell className="text-right w-[304px] min-w-[304px] sticky right-0 z-10 bg-zinc-900 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.8)]">
                            <div className="flex items-center justify-end gap-0.5 flex-nowrap">
                              {/* Bill-to (client legal entity) — choose which billing
                                  profile is the invoice/act recipient. closes backlog f6d26cd9 */}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openBillToDialog(payment)}
                                className={cn(
                                  "h-7 w-7 p-0",
                                  payment.client_billing_profile_id
                                    ? "text-violet-400 hover:text-violet-300 hover:bg-violet-500/10"
                                    : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700/40"
                                )}
                                title={payment.client_billing_profile_label
                                  ? `Bill to: ${payment.client_billing_profile_label} (click to change)`
                                  : "Bill to (client legal entity)"}
                              >
                                <Building2 className="h-3.5 w-3.5" />
                              </Button>

                              {/* Invoice — open dialog if not generated, download badge if generated */}
                              {!payment.invoice_number ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleGenerateInvoiceDirect(payment)}
                                  className="h-7 w-7 p-0 text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                                  title="Generate invoice (uses client FOP + currency)"
                                  disabled={generatingInvoice}
                                >
                                  <FileText className="h-3.5 w-3.5" />
                                </Button>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDownloadInvoice(payment, "docx")}
                                    className="h-7 w-7 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                    title={`Download invoice ${payment.invoice_number} (DOCX)`}
                                    disabled={downloadingInvoice === `${payment.id}:docx`}
                                  >
                                    <FileText className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDownloadInvoice(payment, "pdf")}
                                    className="h-7 px-1 text-[10px] font-semibold text-green-400/80 hover:text-green-300 hover:bg-green-500/10"
                                    title={`Download invoice ${payment.invoice_number} (PDF)`}
                                    disabled={downloadingInvoice === `${payment.id}:pdf`}
                                  >
                                    PDF
                                  </Button>
                                </>
                              )}

                              {/* Act — generate when invoice exists, download badge when generated */}
                              {payment.invoice_number && !payment.act_number && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleGenerateAct(payment)}
                                  className="h-7 w-7 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                                  title="Generate act"
                                  disabled={generatingAct}
                                >
                                  <FileCheck className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {payment.act_number && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDownloadAct(payment, "docx")}
                                    className="h-7 w-7 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                                    title={`Download act ${payment.act_number} (DOCX)`}
                                    disabled={downloadingAct === `${payment.id}:docx`}
                                  >
                                    <FileCheck className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDownloadAct(payment, "pdf")}
                                    className="h-7 px-1 text-[10px] font-semibold text-blue-400/80 hover:text-blue-300 hover:bg-blue-500/10"
                                    title={`Download act ${payment.act_number} (PDF)`}
                                    disabled={downloadingAct === `${payment.id}:pdf`}
                                  >
                                    PDF
                                  </Button>
                                </>
                              )}

                              {/* Demand — sends payment request to client.
                                  Sent state: solid emerald fill + white CheckCircle2 + ring + tooltip
                                  with sender/date. Unsent: amber outline Send icon.
                                  Made unmistakable per 2026-05-22 feedback. */}
                              {(() => {
                                const linkedReq = clientRequests?.find?.(r => r.crm_payment_id === payment.id);
                                const sender = linkedReq?.created_by_name || "—";
                                const sentDate = linkedReq?.created_at?.slice(0, 10);
                                const isSent = sentRequests.has(payment.id);
                                return (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => !isSent && handleSendPaymentRequest(payment)}
                                    disabled={isSent}
                                    className={cn(
                                      "h-7 w-7 p-0 transition-all",
                                      isSent
                                        ? "bg-emerald-500 text-white cursor-default ring-2 ring-emerald-300/70 shadow-md shadow-emerald-500/30"
                                        : "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                                    )}
                                    title={isSent
                                      ? `✓ DEMAND SENT\nBy: ${sender}\nOn: ${sentDate || "today"}`
                                      : "Send payment demand to client"}
                                  >
                                    {isSent ? (
                                      <CheckCircle2 className="h-4 w-4" strokeWidth={2.5} />
                                    ) : (
                                      <Send className="h-3.5 w-3.5" />
                                    )}
                                  </Button>
                                );
                              })()}

                              {/* Bank transaction link */}
                              {payment.bank_transaction_id && payment.bank_transaction_info ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleUnlinkTransaction(payment.id)}
                                  className="h-7 w-7 p-0 text-green-400 hover:text-rose-400 hover:bg-rose-500/10"
                                  title={`Linked to bank tx (${payment.bank_transaction_info.date}) — click to unlink`}
                                >
                                  <Landmark className="h-3.5 w-3.5" />
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleOpenLinkTransaction(payment)}
                                  className="h-7 w-7 p-0 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
                                  title="Link to bank transaction"
                                >
                                  <Landmark className="h-3.5 w-3.5" />
                                </Button>
                              )}

                              {/* Edit payment */}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openPaymentDialog(payment)}
                                className="h-7 w-7 p-0 text-zinc-400 hover:text-zinc-50"
                                title="Edit payment"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>

                              {/* Delete payment */}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeletePayment(payment.id)}
                                className="h-7 w-7 p-0 text-rose-400 hover:text-rose-300"
                                title="Delete payment"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          </>
          )}
        </TabsContent>

        {/* Clients Tab */}
        <TabsContent value="clients">
          {renderClientsCard(clients, true)}
        </TabsContent>

        {/* Warranty support clients (Support only) */}
        {isSupport && (
          <TabsContent value="warranty">
            {renderClientsCard(warrantyClients, false)}
          </TabsContent>
        )}

        {/* Annual hosting clients (Support only) */}
        {isSupport && (
          <TabsContent value="hosting">
            {renderClientsCard(hostingClients, false)}
          </TabsContent>
        )}

        {/* Year View Tab */}
        <TabsContent value="yearly">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-green-500" />
                {selectedYear} Income Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {!stats?.monthly_breakdown?.length ? (
                <div className="text-center py-12 text-zinc-500">
                  <Calendar className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>No data for {selectedYear}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-400 font-medium">Month</TableHead>
                      <TableHead className="text-zinc-400 font-medium text-right">Plan</TableHead>
                      <TableHead className="text-zinc-400 font-medium text-right">Invoiced</TableHead>
                      <TableHead className="text-zinc-400 font-medium text-right">Paid</TableHead>
                      <TableHead className="text-zinc-400 font-medium text-right">Pending</TableHead>
                      <TableHead className="text-zinc-400 font-medium text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.monthly_breakdown.map((row) => (
                      <TableRow 
                        key={row.month} 
                        className={cn(
                          "border-zinc-800 hover:bg-zinc-800/50 cursor-pointer",
                          row.month === selectedMonth && "bg-zinc-800/30"
                        )}
                        onClick={() => {
                          setSelectedMonth(row.month);
                          setActiveTab("payments");
                        }}
                      >
                        <TableCell>
                          <span className="text-zinc-50 font-medium">{formatMonth(row.month)}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono text-zinc-400">{formatAmount(row.planned || 0, "USD")}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono text-orange-400">{formatAmount(row.invoiced || 0, "USD")}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono text-green-400">{formatAmount(row.paid || 0, "USD")}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono text-zinc-500">{formatAmount(row.pending || 0, "USD")}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          {row.pending === 0 && row.paid > 0 ? (
                            <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20">Complete</Badge>
                          ) : row.paid > 0 ? (
                            <Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/20">Partial</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-zinc-800 text-zinc-500 border-zinc-700">Pending</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* TOTAL Row */}
                    {stats.totals && (
                      <TableRow className="border-zinc-800 bg-zinc-800/30 font-bold">
                        <TableCell><span className="text-zinc-50 font-bold">TOTAL</span></TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono text-zinc-300 font-bold">{formatAmount(stats.totals.planned || 0, "USD")}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono text-orange-400 font-bold">{formatAmount(stats.totals.invoiced || 0, "USD")}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono text-green-400 font-bold">{formatAmount(stats.totals.paid || 0, "USD")}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono text-zinc-500 font-bold">{formatAmount((stats.totals.planned - stats.totals.paid - stats.totals.invoiced) || 0, "USD")}</span>
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          
          {/* Extra Payments Stats */}
          {extraStats && extraStats.totals && extraStats.totals.count > 0 && (
            <Card className="bg-zinc-900/50 border-zinc-800 mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-purple-400" />
                  Extra Payments ({selectedYear})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Extra Summary */}
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <p className="text-xs text-zinc-500 uppercase">Total Extra</p>
                    <p className="text-lg font-bold text-purple-400 font-mono">{formatAmount(extraStats.totals.planned || 0, "USD")}</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <p className="text-xs text-zinc-500 uppercase">Actual</p>
                    <p className="text-lg font-bold text-orange-400 font-mono">{formatAmount(extraStats.totals.actual || 0, "USD")}</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <p className="text-xs text-zinc-500 uppercase">Paid</p>
                    <p className="text-lg font-bold text-green-400 font-mono">{formatAmount(extraStats.totals.paid || 0, "USD")}</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <p className="text-xs text-zinc-500 uppercase">Count</p>
                    <p className="text-lg font-bold text-zinc-50 font-mono">{extraStats.totals.count}</p>
                  </div>
                </div>
                
                {/* Extra by Month */}
                {Object.keys(extraStats.by_month || {}).length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800 hover:bg-transparent">
                        <TableHead className="text-zinc-400 font-medium">Month</TableHead>
                        <TableHead className="text-zinc-400 font-medium text-right">Planned</TableHead>
                        <TableHead className="text-zinc-400 font-medium text-right">Actual</TableHead>
                        <TableHead className="text-zinc-400 font-medium text-right">Paid</TableHead>
                        <TableHead className="text-zinc-400 font-medium text-center">Count</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(extraStats.by_month)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([month, data]) => (
                          <TableRow key={month} className="border-zinc-800 hover:bg-zinc-800/50">
                            <TableCell>
                              <span className="text-zinc-50 font-medium">{formatMonth(month)}</span>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="font-mono text-purple-400">{formatAmount(data.planned || 0, "USD")}</span>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="font-mono text-orange-400">{formatAmount(data.actual || 0, "USD")}</span>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="font-mono text-green-400">{formatAmount(data.paid || 0, "USD")}</span>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="bg-zinc-800 text-zinc-400 border-zinc-700">
                                {data.count}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))
                      }
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Bill-to (client legal entity) picker — closes backlog f6d26cd9.
          Mirrors /support-payments: pick which of the client's billing
          profiles is the invoice/act recipient for this CRM payment. */}
      <Dialog open={billToOpen} onOpenChange={setBillToOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">
              Bill to (Client Legal Entity)
            </DialogTitle>
            {billToPayment && (
              <p className="text-xs text-zinc-500 mt-1">
                {billToPayment.client_name} — {billToPayment.month || ""}
              </p>
            )}
          </DialogHeader>

          <div className="py-2 space-y-3">
            {billToLoading ? (
              <div className="text-sm text-zinc-500">Loading client billing profiles…</div>
            ) : (() => {
              const profiles = billToClient?.billing_profiles || [];
              if (profiles.length === 0) {
                return (
                  <div className="text-sm text-amber-400">
                    This client has no billing profiles configured yet.
                    {billToPayment?.client_id && (
                      <>
                        {" "}Open the{" "}
                        <a
                          className="underline hover:text-amber-300"
                          href={`/clients?focus=${billToPayment.client_id}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          client edit page
                        </a>
                        {" "}→ Billing tab to add legal name, EDRPOU, IBAN…
                      </>
                    )}
                  </div>
                );
              }
              const defaultProfile = profiles.find((p) => p.is_default) || profiles[0];
              // Identify a profile by its real legal identity, never a bare
              // "Default" label (the auto-migrated label many clients carry).
              // Priority: legal_name → meaningful label → EDRPOU → fallback.
              const profileLabel = (p) => {
                const ln = (p?.legal_name || "").trim();
                if (ln) return p.edrpou ? `${ln} · EDRPOU ${p.edrpou}` : ln;
                const lb = (p?.label || "").trim();
                if (lb && lb.toLowerCase() !== "default") {
                  return p.edrpou ? `${lb} · EDRPOU ${p.edrpou}` : lb;
                }
                if (p?.edrpou) return `EDRPOU ${p.edrpou}`;
                return "(unnamed profile)";
              };
              const defaultName = profileLabel(defaultProfile);
              // Single legal entity → there is nothing to pick. Show it read-only
              // instead of a misleading "Default / Default" two-option dropdown.
              if (profiles.length === 1) {
                return (
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Recipient on invoice and act</Label>
                    <div className="rounded border border-zinc-800 bg-zinc-950/40 p-2 text-sm text-zinc-200">
                      {profileLabel(profiles[0])}
                    </div>
                    <p className="text-xs text-zinc-500">
                      Single billing entity — every invoice and act is addressed to
                      it. Add more legal entities on the client's Billing tab to
                      choose between them here.
                    </p>
                  </div>
                );
              }
              return (
                <>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Recipient on invoice and act</Label>
                    <Select value={billToValue} onValueChange={setBillToValue}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="default" className="text-zinc-300">
                          Client default{defaultName && defaultName !== "(unnamed profile)" ? ` — ${defaultName}` : ""}
                        </SelectItem>
                        {profiles.map((p) => (
                          <SelectItem key={p.id} value={p.id} className="text-zinc-50">
                            {profileLabel(p)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-zinc-500">
                      Used as the recipient on the invoice and act. "Client default"
                      lets the backend pick the profile marked default for this client.
                    </p>
                  </div>
                  {billToValue !== "default" && (() => {
                    const p = profiles.find((x) => x.id === billToValue);
                    if (!p) return null;
                    return (
                      <div className="rounded border border-zinc-800 bg-zinc-950/40 p-2 text-xs text-zinc-400 space-y-0.5">
                        {p.legal_name && <div><span className="text-zinc-500">Legal name:</span> <span className="text-zinc-300">{p.legal_name}</span></div>}
                        {p.edrpou && <div><span className="text-zinc-500">EDRPOU:</span> <span className="text-zinc-300">{p.edrpou}</span></div>}
                        {p.iban && <div><span className="text-zinc-500">IBAN:</span> <span className="text-zinc-300 font-mono text-[11px]">{p.iban}</span></div>}
                        {p.bank_name && <div><span className="text-zinc-500">Bank:</span> <span className="text-zinc-300">{p.bank_name}</span></div>}
                      </div>
                    );
                  })()}
                </>
              );
            })()}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setBillToOpen(false)}
              className="text-zinc-400 hover:text-zinc-50"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveBillTo}
              disabled={billToSaving || billToLoading || !(billToClient?.billing_profiles || []).length}
              className="bg-sky-600 hover:bg-sky-700 text-white"
            >
              {billToSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <FileText className="h-5 w-5 text-green-400" />
              Import DevBase Data
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-zinc-400 text-sm">
              This will import clients and payment plans from the DevBase CSV file.
            </p>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setImportDialogOpen(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button onClick={handleImport} disabled={importing} className="bg-green-600 hover:bg-green-700">
                {importing ? "Importing..." : "Import Data"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Client Dialog */}
      <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-sky-400" />
              {editingClient ? "Edit Client" : "Add Client"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Company Name *</Label>
              {editingClient ? (
                <Input
                  value={clientForm.name}
                  onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                  placeholder="Company name"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              ) : (
                <>
                  <ClientCombobox
                    clients={allClients}
                    value={clientForm.name}
                    selectedId={clientForm.selected_existing_id}
                    onChange={applyExistingClient}
                    placeholder="Type to search existing or add new..."
                  />
                  {clientForm.selected_existing_id && (() => {
                    const existing = allClients.find((c) => c.id === clientForm.selected_existing_id);
                    const tags = existing?.client_types || [existing?.type].flat().filter(Boolean) || [];
                    const alreadyTagged = tags.includes(category);
                    return (
                      <div className={cn(
                        "text-xs px-3 py-2 rounded border",
                        alreadyTagged
                          ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
                          : "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                      )}>
                        {alreadyTagged ? (
                          <>Already a {category.toUpperCase()} client — Save will update existing fields.</>
                        ) : (
                          <>Existing client (<span className="font-mono">{tags.join(", ") || "no tags"}</span>) — Save will add a {category.toUpperCase()} contract to this same record (no duplicate).</>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
            {/* EDRPOU / tax-id is a billing-profile field — for Support clients
                it's managed in the dedicated /clients page Billing tab, not here.
                Keep it visible for legacy CRM flow only. */}
            {!isSupport && (
              <div className="space-y-2">
                <Label className="text-zinc-400">ЄДРПОУ</Label>
                <Input
                  value={clientForm.edrpou}
                  onChange={(e) => setClientForm({ ...clientForm, edrpou: e.target.value })}
                  placeholder="12345678"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
            )}
            {!isSupport && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Users Count</Label>
                  <Input
                    type="number"
                    min="0"
                    value={clientForm.users_count}
                    onChange={(e) => setClientForm({ ...clientForm, users_count: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Price per User</Label>
                  <Input
                    type="number"
                    min="0"
                    value={clientForm.price_per_user}
                    onChange={(e) => setClientForm({ ...clientForm, price_per_user: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  />
                </div>
              </div>
            )}
            {/* For Support, currency comes from the tariff — no manual override.
                Only FOP (our legal entity for invoicing) stays. */}
            <div className={isSupport ? "" : "grid grid-cols-2 gap-4"}>
              {!isSupport && (
                <div className="space-y-2">
                  <Label className="text-zinc-400">Currency</Label>
                  <Select value={clientForm.currency} onValueChange={(v) => setClientForm({ ...clientForm, currency: v })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="USD" className="text-zinc-50">$ USD</SelectItem>
                      <SelectItem value="EUR" className="text-zinc-50">€ EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-zinc-400">{isSupport ? "FOP (legal entity for invoices)" : "FOP"}</Label>
                <Select value={clientForm.fop || "none"} onValueChange={(v) => setClientForm({ ...clientForm, fop: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                    <SelectValue placeholder="Select FOP" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="none" className="text-zinc-50">— Not selected —</SelectItem>
                    {fops.filter(f => f.is_active).map(fop => (
                      <SelectItem key={fop.id} value={fop.name} className="text-zinc-50">
                        {fop.name}
                      </SelectItem>
                    ))}
                    {fops.length === 0 && (
                      <SelectItem value="no-fops" disabled className="text-zinc-500">
                        No FOPs configured (add in Settings)
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {!isSupport && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Deployment Type</Label>
                  <Select value={clientForm.deployment_type} onValueChange={(v) => setClientForm({ ...clientForm, deployment_type: v })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="cloud" className="text-zinc-50">Cloud</SelectItem>
                      <SelectItem value="box" className="text-zinc-50">Box</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Support Type</Label>
                  <Select value={clientForm.support_type} onValueChange={(v) => setClientForm({ ...clientForm, support_type: v })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="none" className="text-zinc-50">No Support</SelectItem>
                      <SelectItem value="basic" className="text-zinc-50">Basic</SelectItem>
                      <SelectItem value="premium" className="text-zinc-50">Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {clientForm.support_type === "premium" && !isSupport && (
              <div className="space-y-2">
                <Label className="text-zinc-400">Support Price ($)</Label>
                <Input
                  type="number"
                  min="0"
                  value={clientForm.support_price}
                  onChange={(e) => setClientForm({ ...clientForm, support_price: parseInt(e.target.value) || 0 })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
            )}
            {isSupport && (
              <div className="space-y-2">
                <Label className="text-zinc-400">Support Tariff *</Label>
                <Select
                  value={clientForm.support_tariff_id || ""}
                  onValueChange={(v) => {
                    const t = supportTariffs.find((x) => x.id === v);
                    setClientForm({
                      ...clientForm,
                      support_tariff_id: v || null,
                      // Mirror price + currency from tariff into legacy fields so older
                      // invoice paths keep working and total stays consistent.
                      support_price: t ? t.price : 0,
                      currency: t ? (t.currency || "EUR") : "EUR",
                    });
                  }}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                    <SelectValue placeholder="Select a tariff" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700 max-h-72">
                    {supportTariffs.map((t) => {
                      const sym = (t.currency || "EUR") === "EUR" ? "€" : (t.currency || "EUR") === "UAH" ? "₴" : "$";
                      return (
                        <SelectItem key={t.id} value={t.id} className="text-zinc-50">
                          {t.name} — {sym}{(t.price || 0).toLocaleString()}
                          {t.sites_included_label ? ` · ${t.sites_included_label} site(s)` : ""}
                          {t.additional_site_price ? ` · +${sym}${t.additional_site_price}/extra` : ""}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500">
                  Tariff drives price and currency — manage tiers in the{" "}
                  <a href="/settings/support-tariffs" className="text-emerald-400 hover:underline">Support Tariffs</a> catalog.
                </p>
              </div>
            )}
            {isSupport && (
              <div className="space-y-2">
                <Label className="text-zinc-400">Support product</Label>
                <Select
                  value={clientForm.support_product_type || "standard"}
                  onValueChange={(v) => setClientForm({ ...clientForm, support_product_type: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="standard" className="text-zinc-50">Standard support (monthly)</SelectItem>
                    <SelectItem value="warranty" className="text-zinc-50">Warranty support</SelectItem>
                    <SelectItem value="hosting" className="text-zinc-50">Hosting (annual)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500">
                  Groups the client under the Warranty / Hosting tabs.
                </p>
              </div>
            )}
            {isSupport && (clientForm.support_product_type === "warranty" || clientForm.support_product_type === "hosting") && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Service start</Label>
                  <Input
                    type="date"
                    value={clientForm.support_start_date || ""}
                    onChange={(e) => setClientForm({ ...clientForm, support_start_date: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Service end</Label>
                  <Input
                    type="date"
                    value={clientForm.support_end_date || ""}
                    min={clientForm.support_start_date || undefined}
                    onChange={(e) => setClientForm({ ...clientForm, support_end_date: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  />
                  <p className="text-xs text-zinc-500">Tab flags red once this date passes.</p>
                </div>
              </div>
            )}
            {isSupport && (
              <div className="space-y-4 rounded-lg border border-zinc-800 p-3 bg-zinc-950/40">
                <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">
                  Support delivery
                </div>

                {/* Sites on support */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-zinc-400">Sites on support</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs border-zinc-700"
                      onClick={() => setClientForm({
                        ...clientForm,
                        support_sites: [...(clientForm.support_sites || []), { url: "", label: "" }],
                      })}
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add site
                    </Button>
                  </div>
                  {(clientForm.support_sites || []).length === 0 && (
                    <p className="text-xs text-zinc-500 italic">
                      No sites yet — add one or more URLs the team will maintain for this client.
                    </p>
                  )}
                  {(clientForm.support_sites || []).map((site, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        value={site.url || ""}
                        onChange={(e) => {
                          const next = [...clientForm.support_sites];
                          next[i] = { ...next[i], url: e.target.value };
                          setClientForm({ ...clientForm, support_sites: next });
                        }}
                        placeholder="https://example.com"
                        className="bg-zinc-800 border-zinc-700 text-zinc-50 flex-1"
                      />
                      <Input
                        value={site.label || ""}
                        onChange={(e) => {
                          const next = [...clientForm.support_sites];
                          next[i] = { ...next[i], label: e.target.value };
                          setClientForm({ ...clientForm, support_sites: next });
                        }}
                        placeholder="Label (optional)"
                        className="bg-zinc-800 border-zinc-700 text-zinc-50 w-40"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          const next = clientForm.support_sites.filter((_, idx) => idx !== i);
                          setClientForm({ ...clientForm, support_sites: next });
                        }}
                        className="text-rose-400 hover:text-rose-300 h-9 w-9 flex-shrink-0"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Live monthly billing preview */}
                {billingPreview && (billingPreview.total > 0 || billingPreview.base_amount > 0) && (
                  <div className="rounded-md border border-emerald-500/20 bg-emerald-500/5 p-3">
                    <div className="text-xs uppercase tracking-wider text-emerald-300 font-semibold mb-2 flex items-center gap-2">
                      Monthly billing preview
                      {billingPreviewLoading && <span className="text-zinc-500 normal-case">recalculating…</span>}
                    </div>
                    <table className="text-xs w-full">
                      <tbody>
                        {(billingPreview.lines || []).map((line, idx) => {
                          const sym = line.currency === "EUR" ? "€" : line.currency === "UAH" ? "₴" : "$";
                          return (
                            <tr key={idx} className="border-b border-emerald-500/10 last:border-0">
                              <td className="py-1 text-zinc-300">{line.label}</td>
                              <td className="text-right text-zinc-400 font-mono w-16">{line.qty}×</td>
                              <td className="text-right text-zinc-400 font-mono w-20">{sym}{Number(line.unit_price).toLocaleString()}</td>
                              <td className="text-right text-emerald-300 font-mono w-20">{sym}{Number(line.line_total).toLocaleString()}</td>
                            </tr>
                          );
                        })}
                        <tr>
                          <td colSpan={3} className="pt-1.5 text-right text-zinc-400 font-semibold">Total per month</td>
                          <td className="pt-1.5 text-right text-emerald-400 font-mono font-bold">
                            {(billingPreview.currency === "EUR" ? "€" : billingPreview.currency === "UAH" ? "₴" : "$")}
                            {Number(billingPreview.total || 0).toLocaleString()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    {billingPreview.extra_sites > 0 && (
                      <p className="text-xs text-zinc-500 mt-2">
                        {billingPreview.included_sites} site(s) included in tariff · {billingPreview.extra_sites} extra
                        {" "}@ {(billingPreview.extra_currency === "EUR" ? "€" : billingPreview.extra_currency === "UAH" ? "₴" : "$")}{Number(billingPreview.extra_unit_price).toLocaleString()} each
                      </p>
                    )}
                    {/* Warn when admin added more sites than tariff includes but the tariff
                        has no additional-site price set — most common after a legacy tariff
                        was reused; admin needs to fix the tariff in Support Tariffs settings. */}
                    {billingPreview.sites_count > 0 &&
                      billingPreview.sites_count > (billingPreview.included_sites || 0) &&
                      !(billingPreview.extra_unit_price > 0) && (
                      <p className="text-xs text-amber-400 mt-2">
                        ⚠ This tariff has no <strong>Additional Site Price</strong> configured, so extras aren't billed.
                        {" "}<a href="/settings/support-tariffs" target="_blank" rel="noopener" className="underline hover:text-amber-300">Edit tariff</a> to set "Sites Included" and "Additional Site Price".
                      </p>
                    )}
                    {billingPreview.currency_mismatch && (
                      <p className="text-xs text-amber-400 mt-2">
                        ⚠ Extra-site currency differs from tariff currency — review tariff settings.
                      </p>
                    )}
                  </div>
                )}

                {/* Contract */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2 col-span-2">
                    <Label className="text-zinc-400">Contract link</Label>
                    <div className="flex gap-2">
                      <Input
                        value={clientForm.contract_url}
                        onChange={(e) => setClientForm({ ...clientForm, contract_url: e.target.value })}
                        placeholder="https://docs.google.com/document/d/…"
                        className="bg-zinc-800 border-zinc-700 text-zinc-50 flex-1"
                      />
                      {clientForm.contract_url && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-zinc-700 h-9"
                          onClick={() => window.open(clientForm.contract_url, "_blank", "noopener")}
                        >
                          Open
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Contract status</Label>
                    <Select
                      value={clientForm.contract_status || "active"}
                      onValueChange={(v) => setClientForm({ ...clientForm, contract_status: v })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700">
                        <SelectItem value="active" className="text-zinc-50">Active</SelectItem>
                        <SelectItem value="needs_renewal" className="text-zinc-50">Needs renewal</SelectItem>
                        <SelectItem value="missing" className="text-zinc-50">Missing</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Support PM */}
                <div className="space-y-2">
                  <Label className="text-zinc-400">Support PM</Label>
                  <Select
                    value={clientForm.support_pm_id || "none"}
                    onValueChange={(v) => {
                      if (v === "none") {
                        setClientForm({ ...clientForm, support_pm_id: null, support_pm_name: "" });
                      } else {
                        const s = staffList.find((x) => x.id === v);
                        setClientForm({
                          ...clientForm,
                          support_pm_id: v,
                          support_pm_name: s ? s.name : "",
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                      <SelectValue placeholder="Pick an internal owner" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700 max-h-72">
                      <SelectItem value="none" className="text-zinc-500">— No PM assigned —</SelectItem>
                      {staffList.map((s) => (
                        <SelectItem key={s.id} value={s.id} className="text-zinc-50">
                          {s.name}
                          <span className="text-zinc-500 ml-2 text-xs">
                            {(s.role || s.position || "").toLowerCase()}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500">
                    Internal teammate responsible for delivery on this account.
                  </p>
                </div>

                {/* Invoice currency the client is billed in. The issuing FOP
                    comes from the FOP field above (existing client.fop). */}
                <div className="space-y-2 pt-3 border-t border-zinc-800">
                  <Label className="text-zinc-400">Invoice currency</Label>
                  <Select
                    value={clientForm.invoice_currency || "EUR"}
                    onValueChange={(v) => setClientForm({ ...clientForm, invoice_currency: v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700">
                      <SelectItem value="EUR" className="text-zinc-50">€ EUR</SelectItem>
                      <SelectItem value="USD" className="text-zinc-50">$ USD</SelectItem>
                      <SelectItem value="UAH" className="text-zinc-50">₴ UAH</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500">
                    Generate Month copies this onto every monthly invoice. Bank details for the invoice come from the FOP field above.
                  </p>
                </div>
              </div>
            )}
            {!isSupport && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="has_gpt"
                  checked={clientForm.has_gpt}
                  onCheckedChange={(checked) => setClientForm({ ...clientForm, has_gpt: checked })}
                />
                <Label htmlFor="has_gpt" className="text-zinc-400 cursor-pointer">Has GPT Integration</Label>
              </div>
            )}
            {/* Invoice currency — also exposed for CRM clients (DevBase). Drives
                the currency of the generated invoice DOCX; falls back to the
                client.currency field when null. */}
            {!isSupport && (
              <div className="space-y-2">
                <Label className="text-zinc-400">Invoice currency</Label>
                <Select
                  value={clientForm.invoice_currency || clientForm.currency || "USD"}
                  onValueChange={(v) => setClientForm({ ...clientForm, invoice_currency: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="EUR" className="text-zinc-50">€ EUR</SelectItem>
                    <SelectItem value="USD" className="text-zinc-50">$ USD</SelectItem>
                    <SelectItem value="UAH" className="text-zinc-50">₴ UAH</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500">
                  Used by the Invoice DOCX generator. UAH renders the Ukrainian template; USD/EUR — English Invoice.
                </p>
              </div>
            )}
            {/* Contract — Support clients get this block higher up alongside the
                other support fields; CRM clients get it here so a manager can tell
                whether an agreement exists and open it. backlog 604c214e */}
            {!isSupport && (
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2 col-span-2">
                  <Label className="text-zinc-400">Contract link</Label>
                  <div className="flex gap-2">
                    <Input
                      value={clientForm.contract_url}
                      onChange={(e) => setClientForm({ ...clientForm, contract_url: e.target.value })}
                      placeholder="https://docs.google.com/document/d/…"
                      className="bg-zinc-800 border-zinc-700 text-zinc-50 flex-1"
                    />
                    {clientForm.contract_url && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-zinc-700 h-9"
                        onClick={() => window.open(clientForm.contract_url, "_blank", "noopener")}
                      >
                        Open
                      </Button>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Contract status</Label>
                  <Select
                    value={clientForm.contract_status || "active"}
                    onValueChange={(v) => setClientForm({ ...clientForm, contract_status: v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700">
                      <SelectItem value="active" className="text-zinc-50">Active</SelectItem>
                      <SelectItem value="needs_renewal" className="text-zinc-50">Needs renewal</SelectItem>
                      <SelectItem value="missing" className="text-zinc-50">Missing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-zinc-400">Notes</Label>
              {/* Multi-line: notes hold contact details / several facts per
                  client and a single-line Input scrolled them off-screen.
                  backlog a1fa64d8. */}
              <Textarea
                value={clientForm.notes}
                onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })}
                placeholder="Additional notes..."
                className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[90px] whitespace-pre-wrap"
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setClientDialogOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleSaveClient} disabled={savingClient} className="bg-sky-600 hover:bg-sky-700">
              {savingClient ? "Saving..." : (editingClient ? "Update" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-400" />
              {editingPayment ? "Edit Payment" : "Add Payment"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Client *</Label>
              <Select value={paymentForm.client_id} onValueChange={(v) => {
                // Pre-fill currency from the selected client's invoice currency so
                // the modal shows exactly what will be created (WYSIWYG). The user
                // can still override it in the Currency select below. Backend no
                // longer silently rewrites currency (backlog 8bfa7ae7).
                const pool = (paymentForm.payment_type === "extra" && managementClients.length) ? managementClients : clients;
                const c = pool.find(x => x.id === v);
                setPaymentForm({
                  ...paymentForm,
                  client_id: v,
                  currency: c ? (c.invoice_currency || c.currency || paymentForm.currency) : paymentForm.currency,
                });
              }}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                  {/* One-off ("extra") charges may target ANY managed company, not
                      only ones already subscribed to recurring products — use the
                      broader crm+support pool for them; regular payments keep the
                      current category's client list. */}
                  {((paymentForm.payment_type === "extra" && managementClients.length)
                    ? managementClients
                    : clients)
                    .filter((c, idx, arr) => arr.findIndex(x => x.id === c.id) === idx)
                    .map(c => (
                      <SelectItem key={c.id} value={c.id} className="text-zinc-50">{c.name}</SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Month</Label>
                <Input
                  type="month"
                  value={paymentForm.month}
                  onChange={(e) => setPaymentForm({ ...paymentForm, month: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Type</Label>
                <Select value={paymentForm.payment_type} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_type: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="monthly" className="text-zinc-50">Monthly</SelectItem>
                    <SelectItem value="onboarding" className="text-zinc-50">Onboarding</SelectItem>
                    <SelectItem value="extra" className="text-zinc-50">Extra</SelectItem>
                    <SelectItem value="premium_support" className="text-zinc-50">Premium Support</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Currency</Label>
                <Select value={paymentForm.currency} onValueChange={(v) => setPaymentForm({ ...paymentForm, currency: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="USD" className="text-zinc-50">$ USD</SelectItem>
                    <SelectItem value="EUR" className="text-zinc-50">€ EUR</SelectItem>
                    <SelectItem value="UAH" className="text-zinc-50">₴ UAH</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Plan Amount</Label>
                <Input
                  type="number"
                  min="0"
                  value={paymentForm.planned_amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, planned_amount: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Actual Amount</Label>
                <Input
                  type="number"
                  min="0"
                  value={paymentForm.actual_amount || ""}
                  onChange={(e) => setPaymentForm({ ...paymentForm, actual_amount: e.target.value || null })}
                  placeholder="If paid"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Status</Label>
                <Select value={paymentForm.status} onValueChange={(v) => setPaymentForm({ ...paymentForm, status: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="planned" className="text-zinc-50">Planned</SelectItem>
                    <SelectItem value="invoiced" className="text-zinc-50">Invoiced</SelectItem>
                    <SelectItem value="paid" className="text-zinc-50">Paid</SelectItem>
                    <SelectItem value="cancelled" className="text-zinc-50">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 flex items-end pb-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="act_signed"
                    checked={paymentForm.act_signed}
                    onCheckedChange={(checked) => setPaymentForm({ ...paymentForm, act_signed: checked })}
                  />
                  <Label htmlFor="act_signed" className="text-zinc-400 cursor-pointer">Act Signed</Label>
                </div>
              </div>
            </div>

            {/* What the рахунок is for. A one-off charge is never the standard
                subscription line, and there was nowhere to say so — the invoice
                went out with generic wording. backlog 15a89bc6. */}
            <div className="space-y-2">
              <Label className="text-zinc-400">
                Invoice description
                {paymentForm.payment_type === "extra" && <span className="text-amber-400/80 ml-1">— what this one-off covers</span>}
              </Label>
              <Textarea
                value={paymentForm.services_description || ""}
                onChange={(e) => setPaymentForm({ ...paymentForm, services_description: e.target.value })}
                rows={3}
                maxLength={500}
                placeholder="e.g. Доопрацювання модуля звітності DevBase за домовленістю"
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
                data-testid="payment-services-description"
              />
              <p className="text-xs text-zinc-500">
                Printed in «Найменування робіт, послуг» on the рахунок and акт. Leave empty to use the
                standard wording from settings.
              </p>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleSavePayment} disabled={savingPayment} className="bg-green-600 hover:bg-green-700">
              {savingPayment ? "Saving..." : (editingPayment ? "Update" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Bank Transaction Dialog */}
      <Dialog open={linkTransactionDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setLinkTransactionDialogOpen(false);
          setLinkingPaymentId(null);
          setUnmatchedTransactions([]);
        }
      }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Landmark className="h-5 w-5 text-cyan-400" />
              Связать с банковской транзакцией
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {loadingTransactions ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-zinc-400">Загрузка транзакций...</div>
              </div>
            ) : unmatchedTransactions.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <Landmark className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Нет несвязанных транзакций по этому плательщику</p>
                <p className="text-xs mt-1">Совпадение ищется по названию плательщика или ЕДРПОУ клиента</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-zinc-500 mb-3">
                  Найдено {unmatchedTransactions.length} транзакций, совпавших по плательщику / ЕДРПОУ. Выберите нужную:
                </p>
                {unmatchedTransactions.map((tx) => (
                  <div
                    key={tx.id}
                    className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700 hover:border-cyan-600 cursor-pointer transition-colors"
                    onClick={() => handleLinkTransaction(tx.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className="bg-zinc-700 text-zinc-300 text-[10px]">{tx.date}</Badge>
                          <Badge className="bg-blue-900/30 text-blue-400 text-[10px]">{tx.fop_name || "FOP"}</Badge>
                        </div>
                        <p className="text-zinc-50 text-sm font-medium">
                          {tx.counterparty_name || "—"}
                          {tx.counterparty_edrpou && tx.counterparty_edrpou !== "000000000" && (
                            <span className="text-zinc-500 font-normal text-xs ml-2">ЕДРПОУ {tx.counterparty_edrpou}</span>
                          )}
                        </p>
                        <p className="text-zinc-400 text-xs truncate max-w-md" title={tx.purpose}>
                          {tx.purpose?.substring(0, 80) || "—"}{tx.purpose?.length > 80 ? "..." : ""}
                        </p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-green-400 font-mono font-medium">
                          +{tx.amount?.toLocaleString()} {tx.currency}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="border-t border-zinc-800 pt-4 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setLinkTransactionDialogOpen(false);
                setLinkingPaymentId(null);
              }}
              className="border-zinc-700"
            >
              Отмена
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice (Рахунок) Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <FileText className="h-5 w-5 text-orange-400" />
              Створити рахунок
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {invoicePayment && (
              <>
                <div className="bg-zinc-800/50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Клієнт:</span>
                    <span className="text-zinc-50 font-medium">{invoicePayment.client_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Сума:</span>
                    <span className="text-zinc-50 font-mono">
                      {invoicePayment.planned_amount?.toLocaleString()} {invoicePayment.currency || "USD"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Місяць:</span>
                    <span className="text-zinc-50">{formatMonth(invoicePayment.month)}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400">Валюта рахунку *</Label>
                  <Select value={invoiceCurrency} onValueChange={setInvoiceCurrency}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                      <SelectValue placeholder="Виберіть валюту" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="UAH" className="text-zinc-50">UAH (Рахунок-фактура, укр)</SelectItem>
                      <SelectItem value="USD" className="text-zinc-50">USD (Invoice, eng)</SelectItem>
                      <SelectItem value="EUR" className="text-zinc-50">EUR (Invoice, eng)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500">
                    {invoiceCurrency === "UAH" 
                      ? "Рахунок буде створено в UAH (Рахунок-фактура, українська)" 
                      : `Invoice буде створено в ${invoiceCurrency || "USD"} (Invoice, English)`}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-400">Юридична особа (ФОП) *</Label>
                  <Select value={selectedFopForInvoice} onValueChange={setSelectedFopForInvoice}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                      <SelectValue placeholder="Оберіть ФОП" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {fops.filter(f => f.is_active).map(fop => (
                        <SelectItem key={fop.id} value={fop.id} className="text-zinc-50">
                          {fop.name} {fop.full_name ? `(${fop.full_name})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500">
                    Банківські реквізити юр. особи будуть вказані в документі
                  </p>
                </div>
              </>
            )}
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)} className="border-zinc-700">
              Скасувати
            </Button>
            <Button 
              onClick={handleGenerateInvoice} 
              disabled={generatingInvoice || !selectedFopForInvoice}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {generatingInvoice ? "Створюю..." : "Створити рахунок"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrmIncomePage;
